/**
 * AiMinutesPage — AI 회의록 (additive 라우트 /ai-minutes).
 *
 * minutes-svc(:4103) 를 8443 /minutes/* 프록시로 호출. 회사(institution) 공유 목록 +
 * 생성(원문→AI 구조화) + 상세 + 삭제. 검색·상태필터·통계·복사·구조화 상세 뷰.
 *
 * 제약: 이모지 금지(아이콘은 ../../icons 경유 lucide), i18n 은 minutes-i18n,
 * 날짜는 base-i18n formatDate SSOT(lang-aware, toLocale 하드코딩 금지), CSS 토큰만.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ChangeEvent } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Plus, Trash2, Loader2, Sparkles, X, Calendar, Users, Copy, Check, Clock,
  LayoutGrid, List, ChevronLeft, ChevronRight, ArrowLeft, Paperclip, Mic, Download,
} from '../../icons';

type ViewMode = 'card' | 'list';
type AllScope = 'today' | 'recent';

// 대시보드 미리보기 상한 + 전체보기 페이지 크기
const TODAY_PREVIEW = 5;
const RECENT_PREVIEW = 10;
const PAGE_SIZE = 12;
import { formatDate } from '../../lib/base-i18n';
import { useCountUp } from '../../hooks/useCountUp';
import { layerConfirm } from '../../components/LayerPopup/LayerPopup';
import {
  listMinutes, getMinutes, createMinutes, deleteMinutes, MinutesAiFailedError, MinutesForbiddenError,
  type MinutesListItem, type MinutesDetail, type MinutesStatus, type MinutesOrigin,
} from '../../api/minutes';
import { useAuthStore } from '../../store/authStore';
import { useMinutesNotifyStore } from '../../store/minutesNotifyStore';
import { tMinutes, subscribeMinutesLang } from '../../lib/minutes-i18n';
// 보내기(additive · 2026-09-22) — 회의록을 전자결재·메시지·메일로 내보낸다.
import { Send } from '../../icons';
import { MinutesSendPanel } from './MinutesSendPanel';
import { MinutesSentHistory, useMinutesSends } from './MinutesSentHistory';
import type { MinutesSendAck } from '../../api/minutes-send';
// 보낸 뒤 「무엇을 말해도 되나」의 판정 — 한 곳에 두고 부른다(제 시험이 그것을 잠근다).
import { describeSentAck } from './minutes-send-core';
import { InlineNote } from '../../components/shared/InlineNote';
import { Tabs } from '../../components/shared/Tabs';
/*
 * 정리 대기(additive · 2026-10-04 · F1-c) — ★ 녹음은 끝났고 회의록은 «아직 없는» 세션을
 *   이 목록에 함께 보인다. ⛔ 합치는 «규칙»은 awaiting-summary.ts 한 곳에 있다(제 시험이 잠근다).
 * ⚠ 이 파일 아래쪽 OriginBadge 머리말의 「0건」 주석도 같은 판에서 고쳤다 — 이제 1건이다.
 */
import {
  listVoiceSessions, summarizeVoiceSession, getVoiceTranscript, transcriptComplete,
  type VoiceSessionItem, type VoiceTranscript, type TranscriptChunk,
} from '../../api/voice-sessions';
import {
  MINUTES_TABS, rowsForTab, previewRows, tabCounts, type MinutesTab, type MergedRow,
  hasNothingToSummarize, summaryWouldStall,
  // ⭐ 2026-10-20 (시안 ②·③) — 실패 조각의 처지 · 「내부에 녹음된 내역이 없습니다」
  failedChunkState,
  // ⭐ 2026-10-20 (「나」) — 회의록 응답의 녹음 숫자가 먼저 · 없으면 녹음 목록 줄(voiceIsEmpty 는 그 안에서 부른다)
  detailVoiceIsEmpty,
} from './awaiting-summary';
import { AwaitingSummaryRow } from './AwaitingSummaryRow';
import { MinutesSummarizeSay, type SummarizeSaid } from './MinutesSummarizeSay';
import { requestPresignedDownload } from '../../api/files';
// 손짠 머리 → 정본 PageHeader(2026-10-17 · web 레인 W7c — 쪽 머리 한 꼴 F4)
import { PageHeader } from '../../components/shared';
import './AiMinutesPage.css';

const SOURCE_MAX = 100_000;
// 첨부/텍스트 파일 확장자 (음성 STT 는 웹 미지원 — 모바일 앱 전용)
const TEXT_FILE_RE = /\.(txt|md|markdown|csv|log|json|text)$/i;

// 서버 source_text 상한(기본 100,000). JS string.length == UTF-16 코드유닛이라
// 모바일 capSource 와 동일 기준으로 잘라 서버 400(validation) 을 회피한다.
function capSource(s: string): string {
  return s.length > SOURCE_MAX ? s.slice(0, SOURCE_MAX) : s;
}

// 첨부 파일명에서 확장자를 뗀 기본명 — 제목 자동 채움용.
function fileBaseName(name: string): string {
  const i = name.lastIndexOf('.');
  const base = (i > 0 ? name.slice(0, i) : name).trim();
  return base || name;
}

function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function statusLabel(s: MinutesStatus): string {
  return tMinutes(`minutes.status.${s}` as const);
}

export function AiMinutesPage(): JSX.Element {
  // lang 변경 시 리렌더 (task-i18n 패턴)
  const [, forceLang] = useReducer((n: number) => n + 1, 0);
  useEffect(() => subscribeMinutesLang(() => forceLang()), []);

  const [items, setItems] = useState<MinutesListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<MinutesDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  // 전체보기(페이징) 뷰 — null 이면 대시보드. 'today'=오늘 전체, 'recent'=최근 전체.
  const [allView, setAllView] = useState<AllScope | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try { return (localStorage.getItem('minutes_view') as ViewMode) || 'card'; } catch { return 'card'; }
  });
  const setView = useCallback((v: ViewMode) => {
    setViewMode(v);
    try { localStorage.setItem('minutes_view', v); } catch { /* noop */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listMinutes({ limit: 100 });
      setItems(r.items);
      setError(null);
    } catch (err) {
      setError((err as Error)?.message ?? tMinutes('minutes.err.load_failed'));
    } finally {
      setLoading(false);
    }
  }, []);

  /*
   * ── 정리 대기 (additive · 2026-10-04 · F1-c) ───────────────────────────────
   *
   * ⛔⛔ **이 조회가 실패해도 화면은 선다.** 회의록은 minutes-svc(:4103)이고 음성 세션은
   *   chat-server(:3000)다 — **다른 서비스**다. 그래서 한쪽이 죽었을 때 다른 쪽을 끌고
   *   내려가면 **종전에 되던 것이 안 되는** 회귀가 된다.
   * ⇒ ★ 그래서 `error` 를 세우지 «않고» 세션 목록만 빈 채로 둔다 — 탭의 수가 0 이 될 뿐이다.
   */
  const [sessions, setSessions] = useState<VoiceSessionItem[]>([]);
  /**
   * ★★★ 요약이 끝나 ⭐ **띠로 알릴** 회의록 — ⇒ ⛔ 화면을 «바꾸지 않고» 쥐고 있는다.
   *
   * ⚠ 2026-10-08 사용자 결정 ⑦: 「⭐ 긴 회의록을 읽는 중에 ★ 읽던 자리를 잃지 않게」.
   *   ⇒ ⛔ 그래서 다시 읽은 것을 ★ `detail` 에 «바로 넣지 않는다» — 넣으면 ⭐ 보고 있던 글이
   *     눈앞에서 갈아 끼워진다(시안 4절의 「버린 안」이 그 그림이다).
   * ⇒ ★ 「보기」를 누르면 ⭐ 이것을 `detail` 로 옮긴다.
   *   ⚠ 시안은 「보기가 ★ 조회를 ⭐ 한 번 더 부르면 된다」고 적었는데 ⇒ ✅ 이미 ⭐ 손에 있어
   *     ★ 또 부르지 «않는다»(헛조회 0). ⇒ 「새 길이 필요 없다」는 그 뜻은 ★ 그대로 지킨다.
   */
  const [summaryReady, setSummaryReady] = useState<MinutesDetail | null>(null);
  const [summaryAvailable, setSummaryAvailable] = useState(true);
  const [tab, setTab] = useState<MinutesTab>('all');

  const loadSessions = useCallback(async () => {
    try {
      const r = await listVoiceSessions();
      setSessions(r.items);
      setSummaryAvailable(r.summary_available);
    } catch {
      setSessions([]);
      /*
       * ⚠ 엔진 유무를 **모르는** 것이지 「꺼졌다」가 아니다 ⇒ ★ true 로 둔다.
       *   false 로 두면 화면이 「요약 엔진이 꺼져 있다」는 **안 잰 말**을 한다.
       */
      setSummaryAvailable(true);
    }
  }, []);

  useEffect(() => {
    void loadList();
    void loadSessions();
  }, [loadList, loadSessions]);


  /*
   * ★★★ **이음새** — ⭐ 회의록 알림이 오면 ★ «다시 읽는다» (2026-10-09 · 웹 A-1b).
   *
   * ## ⭐ 알림은 ★ 「무언가 바뀌었다」 ⇒ ⭐ 「무엇이」는 ★ 여기서 «본다»
   *
   * 알림에 ⛔ 회의록 id 가 «없다»(`sourceId` 가 `null` — 그 칸이 `BigInt?` 인데 회의록 id 는
   * uuid 문자열이고 담을 다른 칸도 없다). ⇒ ★ 그래서 ⭐ id 로 맞대지 않고 ★ **제 것을 다시 읽어**
   * ⇒ ⭐ 바뀌었나를 본다. ⇒ ⇒ ★ 그러면 ⛔ 「엉뚱한 회의록에 띠」가 ⭐ 안 생긴다 —
   *   ★ 내 것이 안 바뀌었으면 ⇒ ⭐ 띠가 «안 뜬다».
   *
   * ## ⚠ 다시 읽는 것이 ★ **둘**이고, 상세는 ⭐ «열려 있을 때만»
   *
   *     loadList      회의록 목록           ← 늘
   *     loadSessions  「정리 대기」 세션 줄  ← 늘 (⛔ 조회가 «둘»이다 — 하나만 부르면 반만 새로워진다)
   *     getMinutes    열어 둔 상세          ← ⭐ 상세가 «없으면» ⛔ 안 부른다(헛조회다)
   *
   * ⇒ ⚠ 「정리 대기」 줄과 회의록 줄은 ★ `awaiting-summary.ts` 가 **그린 뒤에** 합치는 것이고
   *   ⇒ ⭐ 조회는 ★ 처음부터 **둘**이다(`listMinutes` · `listVoiceSessions`).
   *   ⇒ ⇒ ★ 앱이 같은 함정을 적어 뒀다 — 「⭐ 하나만 부르면 ★ 반만 새로워진다」
   *     (`aos/.../MinutesResync.kt`).
   * ⇒ ⛔ 「전체 보기」 아카이브(`AllListView`)는 ★ 제 쪽수를 쥔 ⭐ 다른 화면이라 ⇒ ★ 이 판에 안 넣었다.
   *
   * ## ⚠ `tick` 이 ★ 올랐을 때«만» 돈다
   *
   * `lastTick` 으로 ★ 막는다. ⇒ ⛔ 안 막으면 ⭐ `detail` 이 바뀔 때마다(탭·보기 누름 등)
   *   ★ 효과가 다시 돌아 ⇒ ⭐ 알림 없이도 조회가 나간다. ⇒ ★ 그것을 시험이 잠근다
   *   (「⭐ 알림이 ★ 안 오면 ⇒ ⛔ 다시 읽지 «않는다»」).
   */
  const notifyTick = useMinutesNotifyStore((s) => s.minutesNotifyTick);
  const lastTick = useRef(0);
  useEffect(() => {
    if (notifyTick === 0 || notifyTick === lastTick.current) return;
    lastTick.current = notifyTick;
    void loadList();
    void loadSessions();
    const open = detail;
    if (open === null) return;
    void (async () => {
      try {
        const fresh = await getMinutes(open.id);
        const had = open.summary != null && open.summary !== '';
        const has = fresh.summary != null && fresh.summary !== '';
        // ⭐ 요약이 «새로» 생겼으면 ★ 띠로 알리고 ⇒ ⛔ 화면은 그대로 둔다(사용자 결정 ⑦).
        if (!had && has) setSummaryReady(fresh);
        else setDetail(fresh);
      } catch {
        // ⛔ 조용히 삼킨다 — 다시 읽기가 실패해도 ★ 보고 있던 것은 ⭐ 그대로 쓸 수 있다.
      }
    })();
  }, [notifyTick, detail, loadList, loadSessions]);

  const openDetail = useCallback(async (id: string) => {
    setCreating(false);
    setDetail(null);
    // ⛔ 다른 회의록을 열 때 ★ 앞의 띠를 ⭐ 남기면 ⇒ ★ 「엉뚱한 회의록에 요약이 왔다」가 된다.
    setSummaryReady(null);
    setDetailLoading(true);
    try {
      setDetail(await getMinutes(id));
    } catch (err) {
      setError((err as Error)?.message ?? tMinutes('minutes.err.load_failed'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  /*
   * ⛔ 「회의록 열기」 깊은 연결을 **두지 않는다** (2026-09-22 사용자 결정).
   *   한때 `?minutes=<id>` 로 상세를 여는 길을 넣었다가 걷어냈다 — 보내는 글에 **링크를 넣지 않기로**
   *   했기 때문이다. 까닭: 회의록 본문은 «작성자만» 본다(9/21 결정)가 그대로 유지돼서, 링크를 주면
   *   받는 사람에게 **아무것도 못 보는 주소**를 준 셈이 된다. 열어 주려면 열람 선을 넓혀야 하는데
   *   사용자가 넓히지 않는 쪽을 택했다. ★ 되살리려면 그 결정부터 다시 받아야 한다.
   */

  const closeOverlay = useCallback(() => {
    setCreating(false);
    setDetail(null);
    setSummaryReady(null);
  }, []);

  const onCreated = useCallback(
    (row: MinutesDetail) => {
      setCreating(false);
      setDetail(row);
      setAllView(null); // 생성 후 대시보드로 — 전체보기 서버 페이지 stale 방지
      void loadList();
    },
    [loadList],
  );

  const onDelete = useCallback(
    async (id: string) => {
      if (!(await layerConfirm(tMinutes('minutes.confirm.delete')))) return;
      try {
        await deleteMinutes(id);
        closeOverlay();
        setAllView(null); // 삭제 후 대시보드로 — 전체보기 서버 페이지 stale 방지
        void loadList();
      } catch (err) {
        // 403 — 작성자만 삭제 가능(모바일 파리티). 그 외는 종전 일반 실패 메시지.
        setError(err instanceof MinutesForbiddenError
          ? tMinutes('minutes.err.delete_forbidden')
          : (err as Error)?.message ?? tMinutes('minutes.err.delete_failed'));
      }
    },
    [loadList, closeOverlay],
  );

  const startCreate = useCallback(() => {
    setDetail(null);
    setCreating(true);
  }, []);

  const todayStr = ymdLocal(new Date());
  const stats = useMemo(() => {
    const weekAgo = Date.now() - 7 * 86_400_000;
    let ready = 0;
    let today = 0;
    let week = 0;
    for (const m of items) {
      if (m.status === 'ready') ready++;
      if (m.meeting_date === todayStr) today++;
      const t = new Date(m.meeting_date ?? m.created_at).getTime();
      if (!Number.isNaN(t) && t >= weekAgo) week++;
    }
    return { all: items.length, ready, today, week };
  }, [items, todayStr]);

  const todayItems = useMemo(
    () => items
      .filter((m) => m.meeting_date === todayStr)
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    [items, todayStr],
  );
  const todayPreview = useMemo(() => todayItems.slice(0, TODAY_PREVIEW), [todayItems]);
  /*
   * ⚠ 종전의 `recentSorted` ＋ `recentPreview` 둘을 **걷어냈다** — 그 자리를 `recentRows`
   *   (`rowsForTab` ＋ `previewRows`)가 맡는다. 같은 자리에 두 벌을 두면 어느 날 한쪽만 고쳐진다.
   * ⚠⚠ 그 둘의 주석은 「전체보기 'today' 가 재사용한다」고 적고 있었는데 **그렇지 않았다** —
   *   전체보기는 `clientItems={allView === 'today' ? todayItems : null}` 로 **`todayItems`** 를
   *   받는다(`recentSorted` 는 `recentPreview` 말고 쓰는 곳이 0건이었다).
   *   ⇒ ★ 지우고 알게 된 것이 아니라 **`tsc` 가 TS6133 으로 말해 주었다**(내 주석이 틀렸다).
   */

  /*
   * 합친 「최근」 — ★ 탭이 고르고, 정리 대기는 «안 잘린다»(previewRows 머리말의 그 까닭).
   * ⚠ 「오늘의 회의」 섹션은 **안 건드린다** — 그 섹션의 축은 `meeting_date`(회의 날짜)이고
   *   음성 세션에는 그 칸이 없다(`started_at` 은 «녹음한» 시각이다). 섞으면 두 날짜가 한 섹션에
   *   들어가 **무슨 날짜인지 알 수 없게** 된다. ⇒ ★ 그 판은 따로 정한다.
   */
  const recentRows = useMemo<MergedRow[]>(
    () => previewRows(rowsForTab(tab, items, sessions), RECENT_PREVIEW),
    [tab, items, sessions],
  );
  const counts = useMemo(() => tabCounts(items, sessions), [items, sessions]);
  /*
   * ⭐ 2026-10-20 (④) — 목록 두 묶음의 머리글(10-10 웹 ㉣)을 걷어 그 수(`groupCounts`)를 여기서 더는 안 읽는다.
   *   ⇒ 탭 수는 여전히 그 함수 «한 출처»에서 나온다(tabCounts 가 부른다).
   */

  const overlayOpen = creating || detailLoading || !!detail;

  return (
    <div className="minutes-page minutes-dash">
      <PageHeader
        className="minutes-dash-pagehead"
        breadcrumb={[tMinutes('minutes.title')]}
        title={tMinutes('minutes.title')}
        description={tMinutes('minutes.count', { n: stats.all })}
        actions={(
          <>
        <div className="minutes-viewtoggle" role="group" aria-label="보기 방식">
          <button type="button" className={`minutes-viewtoggle-btn${viewMode === 'card' ? ' is-active' : ''}`}
            aria-pressed={viewMode === 'card'} title={tMinutes('minutes.dash.view_card')} aria-label={tMinutes('minutes.dash.view_card')}
            onClick={() => setView('card')}>
            <LayoutGrid size={15} aria-hidden />
          </button>
          <button type="button" className={`minutes-viewtoggle-btn${viewMode === 'list' ? ' is-active' : ''}`}
            aria-pressed={viewMode === 'list'} title={tMinutes('minutes.dash.view_list')} aria-label={tMinutes('minutes.dash.view_list')}
            onClick={() => setView('list')}>
            <List size={15} aria-hidden />
          </button>
        </div>
        <button type="button" className="minutes-btn-new" onClick={startCreate}>
          <Plus size={15} aria-hidden /> {tMinutes('minutes.btn.new')}
        </button>
          </>
        )}
      />

      {error && <div className="minutes-error" role="alert">{error}</div>}

      {loading ? (
        <div className="minutes-dash-body"><ListSkeleton /></div>
      ) : allView ? (
        <div className="minutes-dash-body">
          <AllListView
            scope={allView}
            clientItems={allView === 'today' ? todayItems : null}
            mode={viewMode}
            onOpen={openDetail}
            onBack={() => setAllView(null)}
          />
        </div>
      ) : (
        <div className="minutes-dash-body">
          {/* 개요 통계 — v2.11.3: 스태거 진입 + 스트립 드로우 + 카운트업 */}
          <section className="minutes-dash-stats" aria-label={tMinutes('minutes.dash.overview')}>
            <StatCard icon={<FileText size={18} aria-hidden />} label={tMinutes('minutes.dash.stat_total')} value={stats.all} index={0} />
            <StatCard icon={<Calendar size={18} aria-hidden />} label={tMinutes('minutes.dash.stat_today')} value={stats.today} accent="today" index={1} />
            <StatCard icon={<Clock size={18} aria-hidden />} label={tMinutes('minutes.dash.stat_week')} value={stats.week} accent="week" index={2} />
            <StatCard icon={<Check size={18} aria-hidden />} label={tMinutes('minutes.dash.stat_ready')} value={stats.ready} accent="ready" index={3} />
          </section>

          {/* 오늘의 회의 — 최대 5개 미리보기 + 전체보기 */}
          <section className="minutes-dash-sec">
            <h2 className="minutes-dash-sec-title">
              <Calendar size={16} aria-hidden /> {tMinutes('minutes.dash.today')}
              <span className="minutes-dash-sec-count">{todayItems.length}</span>
              {todayItems.length > TODAY_PREVIEW && (
                <button type="button" className="minutes-dash-viewall" onClick={() => setAllView('today')}>
                  {tMinutes('minutes.dash.viewall')}
                </button>
              )}
            </h2>
            {todayPreview.length ? (
              <MeetingCollection items={todayPreview} mode={viewMode} onOpen={openDetail} />
            ) : (
              <div className="minutes-dash-empty">{tMinutes('minutes.dash.today_empty')}</div>
            )}
          </section>

          {/* 최근 회의록 — 최대 10개 미리보기 + 전체보기 · ★ 2026-10-04: 상태 탭 셋 ＋ 정리 대기 합치기 */}
          <section className="minutes-dash-sec">
            <h2 className="minutes-dash-sec-title">
              <FileText size={16} aria-hidden /> {tMinutes('minutes.dash.recent')}
              <span className="minutes-dash-sec-count">{counts.all}</span>
              {/*
                ⛔ 「전체 보기」는 **정리 대기 탭에서 숨긴다** — 그 화면은 회의록만 그린다
                  (서버 페이징 축이 minutes-svc 의 offset 이다). 거기로 보내면 사용자는
                  「정리 대기를 눌렀는데 사라졌다」를 겪는다.
                ⚠ 그래도 잃는 것은 없다 — 정리 대기는 이 섹션에서 **늘 전부** 보인다.
              */}
              {tab !== 'awaiting' && stats.all > RECENT_PREVIEW && (
                <button type="button" className="minutes-dash-viewall" onClick={() => setAllView('recent')}>
                  {tMinutes('minutes.dash.viewall')}
                </button>
              )}
            </h2>
            <div className="minutes-tabs" role="tablist" aria-label={tMinutes('minutes.tab.aria')}>
              {MINUTES_TABS.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={tab === t}
                  className={`minutes-tab${tab === t ? ' minutes-tab--on' : ''}`}
                  onClick={() => setTab(t)}
                >
                  {tMinutes(`minutes.tab.${t}` as const)}
                  <span className="minutes-tab-count">{counts[t]}</span>
                </button>
              ))}
            </div>
            {recentRows.length ? (
              <MergedCollection
                rows={recentRows}
                mode={viewMode}
                onOpen={openDetail}
              />
            ) : (
              <div className="minutes-dash-empty">
                {tab === 'awaiting'
                  ? tMinutes('minutes.awaiting.empty')
                  : tMinutes('minutes.dash.recent_empty')}
              </div>
            )}
          </section>
        </div>
      )}

      {/* 상세/생성 모달 오버레이 */}
      {overlayOpen && (
        <div className="minutes-modal-backdrop" role="presentation" onClick={closeOverlay}>
          <div className="minutes-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="minutes-modal-close" onClick={closeOverlay} aria-label={tMinutes('minutes.btn.cancel')}>
              <X size={18} aria-hidden />
            </button>
            {creating ? (
              <MinutesCreateForm onCreated={onCreated} onCancel={closeOverlay} />
            ) : detailLoading ? (
              <div className="minutes-detail-empty"><Loader2 size={28} className="minutes-spin" aria-hidden /></div>
            ) : detail ? (
              <MinutesDetailView
                detail={detail}
                onDelete={() => void onDelete(detail.id)}
                sessions={sessions}
                summaryAvailable={summaryAvailable}
                summaryReady={summaryReady !== null}
                onApplySummary={() => {
                  if (summaryReady === null) return;
                  setDetail(summaryReady);
                  setSummaryReady(null);
                }}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 전체보기(페이징) 뷰.
 *   - scope='today': clientItems(메모리상 오늘 목록)을 클라이언트 페이징. 당일 한정이라 서버
 *     날짜필터 불필요 + 이미 로드된 데이터 재사용.
 *   - scope='recent': 전체 아카이브이므로 서버 페이징(listMinutes limit/offset, total). minutes-svc
 *     가 created_at DESC 정렬을 보장하므로 대시보드 "최근" 정렬과 일치.
 */
function AllListView({
  scope, clientItems, mode, onOpen, onBack,
}: {
  scope: AllScope;
  clientItems: MinutesListItem[] | null;
  mode: ViewMode;
  onOpen: (id: string) => void;
  onBack: () => void;
}): JSX.Element {
  const [page, setPage] = useState(0); // 0-based
  const [serverItems, setServerItems] = useState<MinutesListItem[]>([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [pageLoading, setPageLoading] = useState(scope === 'recent');
  const [pageError, setPageError] = useState<string | null>(null);

  // recent: 서버 페이지 로드
  useEffect(() => {
    if (scope !== 'recent') return;
    let cancelled = false;
    setPageLoading(true);
    listMinutes({ limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then((r) => {
        if (cancelled) return;
        setServerItems(r.items);
        setServerTotal(r.total);
        setPageError(null);
      })
      .catch((err) => { if (!cancelled) setPageError((err as Error)?.message ?? tMinutes('minutes.err.load_failed')); })
      .finally(() => { if (!cancelled) setPageLoading(false); });
    return () => { cancelled = true; };
  }, [scope, page]);

  const total = scope === 'today' ? (clientItems?.length ?? 0) : serverTotal;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = scope === 'today'
    ? (clientItems ?? []).slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)
    : serverItems;

  const title = scope === 'today' ? tMinutes('minutes.dash.today_all') : tMinutes('minutes.dash.recent_all');
  const empty = scope === 'today' ? tMinutes('minutes.dash.today_empty') : tMinutes('minutes.dash.recent_empty');

  return (
    <section className="minutes-allview">
      <div className="minutes-allview-head">
        <button type="button" className="minutes-allview-back" onClick={onBack}>
          <ArrowLeft size={15} aria-hidden /> {tMinutes('minutes.dash.back')}
        </button>
        <h2 className="minutes-allview-title">
          {title}
          <span className="minutes-dash-sec-count">{total}</span>
        </h2>
      </div>

      {pageError && <div className="minutes-error" role="alert">{pageError}</div>}

      {pageLoading ? (
        <ListSkeleton />
      ) : pageItems.length ? (
        <MeetingCollection items={pageItems} mode={mode} onOpen={onOpen} />
      ) : (
        <div className="minutes-dash-empty">{empty}</div>
      )}

      {totalPages > 1 && (
        <nav className="minutes-pager" aria-label={title}>
          <button type="button" className="minutes-pager-btn" disabled={page <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}>
            <ChevronLeft size={15} aria-hidden /> {tMinutes('minutes.page.prev')}
          </button>
          <span className="minutes-pager-ind">
            {tMinutes('minutes.page.indicator', { cur: page + 1, total: totalPages })}
          </span>
          <button type="button" className="minutes-pager-btn" disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>
            {tMinutes('minutes.page.next')} <ChevronRight size={15} aria-hidden />
          </button>
        </nav>
      )}
    </section>
  );
}

function StatCard({ icon, label, value, accent, index = 0 }: { icon: JSX.Element; label: string; value: number; accent?: string; index?: number }): JSX.Element {
  const shown = useCountUp(value, 800);
  return (
    <div
      className={`minutes-stat${accent ? ` minutes-stat--${accent}` : ''} minutes-stat--anim`}
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <span className="minutes-stat-icon" aria-hidden>{icon}</span>
      <span className="minutes-stat-value">{shown}</span>
      <span className="minutes-stat-label">{label}</span>
    </div>
  );
}

function MeetingCollection({ items, mode, onOpen }: { items: MinutesListItem[]; mode: ViewMode; onOpen: (id: string) => void }): JSX.Element {
  if (mode === 'list') {
    return (
      <div className="minutes-dash-listview">
        {items.map((m, i) => <MeetingRow key={m.id} m={m} index={i} onClick={() => void onOpen(m.id)} />)}
      </div>
    );
  }
  return (
    <div className="minutes-dash-grid">
      {items.map((m, i) => <MeetingCard key={m.id} m={m} index={i} onClick={() => void onOpen(m.id)} />)}
    </div>
  );
}

/**
 * 합친 목록 — 회의록 줄과 **「정리 대기」 줄**을 한 흐름에 그린다 (2026-10-04 · F1-c).
 *
 * ⛔ `MeetingCollection` 을 **고치지 않고** 옆에 둔다 — 그 부품은 「오늘의 회의」와
 *   「전체 보기」가 아직 쓰고 있고, 그 둘은 `MinutesListItem[]` 만 받는다.
 *   ⇒ ★ 거기에 `MergedRow` 를 밀어넣으면 **그 두 화면의 타입이 함께 넓어진다**(회귀 위험).
 *
 * ★ `kind` 로 고르므로 **그리는 쪽이 다시 판정하지 않는다**(`awaiting-summary.ts` 가 판정한다).
 * ⚠ 카드 보기에서도 「정리 대기」는 **줄 모양**으로 그린다 — 단추가 들어가는 줄이라
 *   카드 안에 넣으면 「카드 전체가 눌리는 것」과 「단추만 눌리는 것」이 섞인다.
 */
function MergedCollection({
  rows, mode, onOpen,
}: {
  rows: MergedRow[];
  mode: ViewMode;
  onOpen: (id: string) => void;
}): JSX.Element {
  /*
   * ⭐⭐ 2026-10-20 (④ · 사용자 결정) — «한 목록»에 «받은 차례 그대로»(rowsForTab 이 날짜순으로 섞는다) 그린다.
   *   사용자 원문: 「웹에서만 전체, 정리대기 탭에서 보면 상단에 정리대기로 표시된다. 다른 목록과 동일하게
   *   디자인하고 대기로 표시해달라.」 ＋ 머리글은 「없애고 날짜순」.
   *   ⇒ 녹음 줄은 회의록 줄과 같은 모양 · 배지 「대기」(AwaitingSummaryRow) · 카드 보기에서는 카드로.
   *   ⇒ 그리고 녹음 줄에 「요약하기」를 «두지 않는다»(시안 ④ 후 A · 사용자 결정) — 요약은 회의록 상세 「요약」 탭.
   * ⛔ 종전(10-10 웹 ㉣): 정리 대기를 위에 모으고 「정리 대기 N」·「회의록 N」 머리글을 달았다 — 까닭은
   *   정리 대기가 길면 회의록이 밀려 «없는 것처럼» 보였기 때문이다. ⇒ 섞으면 그 밀림 자체가 없다.
   */
  const drawRow = (r: MergedRow, i: number): JSX.Element => (r.kind === 'awaiting'
    ? (
      <AwaitingSummaryRow
        key={`s:${r.session.session_id}`}
        s={r.session}
        index={i}
        mode={mode}
      />
    )
    : mode === 'list'
      ? <MeetingRow key={`m:${r.minutes.id}`} m={r.minutes} index={i} onClick={() => void onOpen(r.minutes.id)} />
      : <MeetingCard key={`m:${r.minutes.id}`} m={r.minutes} index={i} onClick={() => void onOpen(r.minutes.id)} />);
  return (
    <div className={mode === 'list' ? 'minutes-dash-listview' : 'minutes-dash-grid'}>
      {rows.map(drawRow)}
    </div>
  );
}

function MeetingRow({ m, index = 0, onClick }: { m: MinutesListItem; index?: number; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      className={`minutes-row minutes-row--anim minutes-item--st-${m.status}`}
      style={{ animationDelay: `${Math.min(index, 10) * 50}ms` }}
      onClick={onClick}
    >
      <span className="minutes-row-ico" aria-hidden><FileText size={15} /></span>
      <span className="minutes-row-title">{m.title}</span>
      <span className="minutes-row-meta">
        {m.meeting_date && <span className="minutes-chip"><Calendar size={12} aria-hidden />{formatDate(m.meeting_date)}</span>}
        <span className="minutes-chip"><Users size={12} aria-hidden />{tMinutes('minutes.attendees_count', { n: m.attendees.length })}</span>
      </span>
      <span className="minutes-row-created">{formatDate(m.created_at)}</span>
      <OriginBadge origin={m.origin} />
      <StatusBadge status={m.status} />
    </button>
  );
}

function MeetingCard({ m, index = 0, onClick }: { m: MinutesListItem; index?: number; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      className={`minutes-meetcard minutes-meetcard--anim minutes-item--st-${m.status}`}
      style={{ animationDelay: `${Math.min(index, 10) * 60}ms` }}
      onClick={onClick}
    >
      <div className="minutes-card-top">
        <span className="minutes-card-title">{m.title}</span>
        <OriginBadge origin={m.origin} />
        <StatusBadge status={m.status} />
      </div>
      <div className="minutes-card-meta">
        {m.meeting_date && (
          <span className="minutes-chip"><Calendar size={12} aria-hidden />{formatDate(m.meeting_date)}</span>
        )}
        <span className="minutes-chip"><Users size={12} aria-hidden />{tMinutes('minutes.attendees_count', { n: m.attendees.length })}</span>
      </div>
      <div className="minutes-card-created">{tMinutes('minutes.list.created', { date: formatDate(m.created_at) })}</div>
    </button>
  );
}

function ListSkeleton(): JSX.Element {
  return (
    <div className="minutes-skeleton-wrap" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="minutes-skeleton-card">
          <div className="minutes-skeleton-line w70" />
          <div className="minutes-skeleton-line w40" />
        </div>
      ))}
    </div>
  );
}

/**
 * 「녹음에서 왔다」 — ★ 음성 회의록에만 붙는다 (2026-09-22).
 *
 * ## ★★ 왜 표시가 필요한가 — 섞어 보이기로 했기 때문이다
 *
 * 음성 회의록은 손으로 쓴 것과 **가진 것이 다르다**: `source_text` 가 **없고**(NULL) `model` 도
 * null 이다. 표시가 없으면 사용자는 **「왜 이건 원문이 없나」**를 묻게 된다 — 이 배지 하나가 그 답이다.
 *
 * ⛔ 탭으로 «가르지» 않는다: 화면 이름이 「AI 회의록」인데 가르면 **한눈에 안 보이고**,
 *   사용자는 「내 회의록이 어디 갔나」를 묻는다.
 *
 * ★ 판정을 **한 곳**에 둔다 — 목록 줄·카드·상세 셋이 이 부품을 부른다(세 글자로 적으면 어느 날
 *   한 곳만 고쳐진다).
 */
function OriginBadge({ origin }: { origin?: MinutesOrigin }): JSX.Element | null {
  // ⚠ 옛 서버는 이 칸을 안 준다(`undefined`) → 그때는 「손으로 쓴 것」이라 아무것도 안 그린다.
  if (origin !== 'voice_memo') return null;
  return (
    <span className="minutes-chip minutes-chip--voice">
      <Mic size={12} aria-hidden /> {tMinutes('minutes.origin.voice')}
    </span>
  );
}

function StatusBadge({ status }: { status: MinutesStatus }): JSX.Element {
  return <span className={`minutes-status minutes-status--${status}`}>{statusLabel(status)}</span>;
}

function useCopy(): { copiedKey: string | null; copy: (key: string, text: string) => void } {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copy = useCallback((key: string, text: string) => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    });
  }, []);
  return { copiedKey, copy };
}

function CopyButton({ id, text, copiedKey, copy }: { id: string; text: string; copiedKey: string | null; copy: (k: string, t: string) => void }): JSX.Element {
  const done = copiedKey === id;
  return (
    <button type="button" className="minutes-copy-btn" onClick={() => copy(id, text)} aria-label={tMinutes('minutes.copy')}>
      {done ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
      {done ? tMinutes('minutes.copied') : tMinutes('minutes.copy')}
    </button>
  );
}

/**
 * 「회의록」 탭 — ★ 뽑을 수 있는 ⭐ «문서»다. (⇒ 양식정리 ＋ 회의 내용)
 *
 * ## ⭐⭐ 2026-10-09 — ★ 이 탭의 «이름»이 ⇒ ⭐ 「전체」 → 「회의록」으로 바뀌었다 (사용자 결정 ⓑ)
 *
 * ⛔ 웹의 「전체」와 앱의 「전체」가 ★ 같은 낱말로 ⭐ 다른 것을 가리켰다 —
 *   웹은 ★ «문서»(이 패널), 앱은 ⭐ «시간별 전사». ⇒ ⇒ ★ 사용자가 「웹도 앱처럼」을 골랐다.
 * ⇒ ★ 그래서 ⭐ 이 패널은 `minutes.dtab.doc`(「회의록」)이 되고,
 *   ⇒ ⭐ 「전체」라는 낱말은 ★ 새 `MinutesTranscriptPanel` 로 ⭐ «옮겨 갔다».
 * ⇒ ⚠ 안의 글자는 ⭐ 한 줄도 안 고쳤다 — `minutes-body-extract.test.tsx` 가 그것을 잰다.
 *
 * ⭐ 2026-10-09 — `MinutesDetailBody` 에서 ★ 글자 그대로 갈라냈다(#2328 이 옮겨 둔 그 168줄에서
 * 「보낸 이력」 2줄만 ★ 「회의정보」로 나갔다). ⇒ ⛔ 안의 글자는 ★ 한 줄도 안 고쳤다.
 *
 * ⚠ 이 탭과 「요약」 탭은 ★ 같은 것을 ⭐ 두 꼴로 보여 준다 — ⇒ 사용자 결정 ②(시안 3절):
 *   ```
 *   전체   ★ 표 — ⭐ 뽑아서 ★ 서명하는 양식(소속·직급·서명 칸이 ⭐ 일부러 빈 칸이다)
 *   요약   ★ 글과 줄 — ⭐ 읽는 자리
 *   ```
 *   ⇒ ⛔ 그래서 ★ 양식을 ⭐ «다시 그리지 않았다» — 마크업이 ★ 한 벌로 남는다.
 */
function MinutesDocPanel({ detail, copiedKey, copy, showSource, setShowSource }: {
  detail: MinutesDetail;
  copiedKey: string | null;
  copy: (k: string, t: string) => void;
  showSource: boolean;
  setShowSource: React.Dispatch<React.SetStateAction<boolean>>;
}): JSX.Element {
  const attendees = detail.attendees.length > 0 ? detail.attendees : null;
  return (
    <>
      {/* 양식정리 — doc1 회의록 표 + doc2 참석자 명단 표 (docs/doc1.png·doc2.png 레이아웃) */}
      <section className="minutes-block minutes-form-block">
        <div className="minutes-block-head">
          <h3><span className="minutes-block-ico"><Sparkles size={14} aria-hidden /></span> {tMinutes('minutes.section.form')}</h3>
          {detail.summary && <CopyButton id="summary" text={detail.summary} copiedKey={copiedKey} copy={copy} />}
        </div>

        {/* doc1 — 회의록 양식 */}
        <div className="minutes-doc">
          <div className="minutes-doc-title">{tMinutes('minutes.form.doc_title')}</div>
          <table className="minutes-doc-table">
            <colgroup>
              <col style={{ width: '15%' }} /><col style={{ width: '43%' }} />
              <col style={{ width: '14%' }} /><col style={{ width: '14%' }} /><col style={{ width: '14%' }} />
            </colgroup>
            <tbody>
              <tr>
                <th className="mdoc-lbl">{tMinutes('minutes.form.no')}</th>
                <td className="mdoc-val">{detail.id.slice(0, 8).toUpperCase()}</td>
                <th className="mdoc-hd">{tMinutes('minutes.form.author')}</th>
                <th className="mdoc-hd">{tMinutes('minutes.form.reviewer')}</th>
                <th className="mdoc-hd">{tMinutes('minutes.form.approver')}</th>
              </tr>
              <tr>
                <th className="mdoc-lbl">{tMinutes('minutes.form.topic')}</th>
                <td className="mdoc-val">{detail.title}</td>
                <td className="mdoc-sign" rowSpan={3} aria-hidden />
                <td className="mdoc-sign" rowSpan={3} aria-hidden />
                <td className="mdoc-sign" rowSpan={3} aria-hidden />
              </tr>
              <tr>
                <th className="mdoc-lbl">{tMinutes('minutes.form.datetime')}</th>
                <td className="mdoc-val">{formatDate(detail.meeting_date ?? detail.created_at)}</td>
              </tr>
              <tr>
                <th className="mdoc-lbl">{tMinutes('minutes.form.place')}</th>
                <td className="mdoc-val" />
              </tr>
              <tr>
                <th className="mdoc-lbl mdoc-content-lbl">{tMinutes('minutes.form.content')}</th>
                <td className="mdoc-content" colSpan={4}>
                  {/* 요약 — 회의록 문장형 문단 */}
                  {detail.summary
                    ? <p className="mdoc-narrative">{detail.summary}</p>
                    : <p className="minutes-muted">{tMinutes('minutes.detail.no_summary')}</p>}

                  {/* 결정사항 — 문서형 번호 목록 */}
                  {detail.decisions.length > 0 && (
                    <div className="mdoc-sec">
                      <h4 className="mdoc-sec-title">{tMinutes('minutes.section.decisions')}</h4>
                      <ol className="mdoc-list">
                        {detail.decisions.map((d, i) => <li key={i}>{d}</li>)}
                      </ol>
                    </div>
                  )}

                  {/* 액션아이템 — 문서형 문장(담당·기한 인라인) */}
                  {detail.action_items.length > 0 && (
                    <div className="mdoc-sec">
                      <h4 className="mdoc-sec-title">{tMinutes('minutes.section.actions')}</h4>
                      <ol className="mdoc-list">
                        {detail.action_items.map((a, i) => {
                          const meta = [
                            a.assignee ? `${tMinutes('minutes.form.assignee')}: ${a.assignee}` : null,
                            a.due ? `${tMinutes('minutes.form.due')}: ${a.due}` : null,
                          ].filter(Boolean).join(', ');
                          return (
                            <li key={i}>
                              {a.text}
                              {meta && <span className="mdoc-meta"> ({meta})</span>}
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* doc2 — 회의 참석자 명단 */}
        <div className="minutes-doc minutes-doc--attendees">
          <div className="minutes-doc-subtitle">{tMinutes('minutes.form.attendee_list')}</div>
          <table className="minutes-doc-table">
            <colgroup>
              <col style={{ width: '12%' }} /><col style={{ width: '30%' }} />
              <col style={{ width: '28%' }} /><col style={{ width: '15%' }} /><col style={{ width: '15%' }} />
            </colgroup>
            <thead>
              <tr>
                <th className="mdoc-hd">{tMinutes('minutes.form.rowno')}</th>
                <th className="mdoc-hd">{tMinutes('minutes.form.affiliation')}</th>
                <th className="mdoc-hd">{tMinutes('minutes.form.name')}</th>
                <th className="mdoc-hd">{tMinutes('minutes.form.position')}</th>
                <th className="mdoc-hd">{tMinutes('minutes.form.signature')}</th>
              </tr>
            </thead>
            <tbody>
              {attendees
                ? attendees.map((a, i) => (
                  <tr key={i}>
                    <td className="mdoc-no">{i + 1}</td>
                    <td />
                    <td className="mdoc-name">{a}</td>
                    <td />
                    <td />
                  </tr>
                ))
                : (
                  <tr>
                    <td className="mdoc-empty" colSpan={5}>{tMinutes('minutes.form.no_attendees')}</td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </section>

      {/*
        ⚠ **이 자리에 있던 두 알림(`logged === false` · `truncated`)은 «위»로 옮겼다** (2026-09-22).
          같은 말을 두 곳에서 하지 않기 위해서다 — 그 둘은 이제 「보냈습니다」와 **한 덩이**로
          머리 밑에서 함께 말한다. 까닭: 표 아래는 보내고 돌아온 사람의 눈에 안 들어오는 자리였고,
          「보냈다」 없이 「이력에 안 남았다」만 말하면 **반쪽**이다.
      */}

      {/*
        ★★★ **원문이 «없는» 회의록이 있다** (2026-09-22 — 음성에서 온 것).

        마이그 0005 가 `source_text` 의 `NOT NULL` 을 풀었고, 그 주석이 까닭을 적었다 —
        「⛔ «비어 있다»가 아니라 «여기 있는 것이 아니다»」. 음성 경로의 재료는 **녹음 조각**이고
        회의록에 실려 오는 것은 **결과**다.

        ⛔ 종전 판은 그래도 「원문 보기」를 그려 **빈 상자**를 보여 줬다 — 화면이 거짓말을 한 것이다.
        ⚠ `tsc` 가 못 잡는다: `<pre>{null}</pre>` 은 **문법상 적법**하다. 타입을 고쳐도 조용하다.
      */}
      {detail.source_text !== null && detail.source_text !== '' ? (
        <section className="minutes-block minutes-block--source">
          <button type="button" className="minutes-source-toggle" onClick={() => setShowSource((v) => !v)}>
            <FileText size={14} aria-hidden />
            {showSource ? tMinutes('minutes.source.hide') : tMinutes('minutes.source.show')}
          </button>
          {showSource && <pre className="minutes-source">{detail.source_text}</pre>}
        </section>
      ) : (
        <section className="minutes-block minutes-block--source">
          <p className="minutes-muted">
            {detail.origin === 'voice_memo'
              ? tMinutes('minutes.source.none_voice')
              : tMinutes('minutes.source.none')}
          </p>
          {/*
            ⚠ **「녹음을 지워도 이 회의록은 남습니다」** — 8443 에는 녹음을 지우는 자리가 «없지만»
              (2026-10-04 정정: `voice/sessions` 를 «읽는» 곳은 이제 1건이다 — `api/voice-sessions.ts`
               의 `listVoiceSessions`. ⛔ 그래도 **지우는 곳은 여전히 모바일뿐**이다 —
               이 판은 «읽기»와 «요약 걸기»만 더했고 DELETE 는 0건이다) 모바일이 그 문구를
              시안에 넣었으므로 **여기서도 같은 말을 해야** 두 화면이 어긋나지 않는다.
              ⇒ 사용자가 모바일에서 녹음을 지우고 이 화면에 와서 「왜 남았나」 하지 않게 한다.
          */}
          {detail.origin === 'voice_memo' && (
            <p className="minutes-muted">{tMinutes('minutes.source.voice_kept')}</p>
          )}
        </section>
      )}
    </>
  );
}

/**
 * 「회의정보」 탭 — ⭐ 처음 열면 ★ 이것이 뜬다 (2026-10-08 사용자 결정 · 시안 2절).
 *
 * ## ⛔ 「소속」 칸을 ★ 안 그렸다 — ⇒ ⭐ 그 데이터가 «없다»
 *
 * 시안 2절의 그림에는 ★ 「이름 | 소속 / 오영진 | 개발1팀」이 있다. ⇒ ⛔ 그런데 재 보니
 * `MinutesDetail.attendees` 는 ★ `string[]`(이름뿐)이고, 「전체」 탭 양식의 소속·직급·서명 칸은
 * ⭐ 일부러 ★ «빈 칸»이다 — 뽑아서 ★ 손으로 적고 서명하는 자리다(떠 온 DOM 으로 확인했다).
 * ⇒ ★ 그래서 ⭐ 이름만 그린다. ⛔ 없는 칸을 ★ 「개발1팀」처럼 ⭐ 지어 채우지 않는다.
 *
 * ## ⚠ 「만든 방법」은 ★ 알 때만 그린다
 *
 * `origin` 은 ★ 선택 칸(`origin?`)이다. ⇒ ⛔ 모를 때 ★ 「직접 입력」이라고 ⭐ 적으면 거짓이 된다
 *   (음성에서 온 것도 옛 서버는 이 칸을 안 준다). ⇒ ★ 그래서 ⭐ «모르면 그 줄을 숨긴다».
 *
 * ## ⭐ 「보낸 이력」이 ★ 이 탭으로 왔다 (시안 2절 — 「보낸 이력 여기로 옮김」)
 *
 * ⚠ 그 두 줄은 ★ #2328 이 옮겨 둔 자리에서 ⭐ «글자 그대로» 왔다. ⇒ 되돌리기가 없어 ★ 그것이
 *   유일한 되짚을 길이라는 ⭐ 그 주석도 ★ 함께 왔다.
 */
function MinutesInfoPanel({ detail, sends }: {
  detail: MinutesDetail;
  sends: ReturnType<typeof useMinutesSends>;
}): JSX.Element {
  return (
    <>
      <section className="minutes-block minutes-info-block">
        <dl className="minutes-info">
          <div className="minutes-info-row">
            <dt>{tMinutes('minutes.info.title')}</dt>
            <dd>{detail.title}</dd>
          </div>
          <div className="minutes-info-row">
            <dt>{tMinutes('minutes.info.date')}</dt>
            <dd>{formatDate(detail.meeting_date ?? detail.created_at)}</dd>
          </div>
          <div className="minutes-info-row">
            <dt>{tMinutes('minutes.info.state')}</dt>
            <dd><StatusBadge status={detail.status} /></dd>
          </div>
          {/* ⚠ 모르면 숨긴다 — 위 머리말. */}
          {detail.origin != null && (
            <div className="minutes-info-row">
              <dt>{tMinutes('minutes.info.made')}</dt>
              <dd><OriginBadge origin={detail.origin} /></dd>
            </div>
          )}
          <div className="minutes-info-row">
            <dt>{tMinutes('minutes.info.attendees')}</dt>
            <dd>
              {detail.attendees.length > 0
                ? (
                  <span className="minutes-attendees">
                    {detail.attendees.map((a, i) => (
                      <span key={i} className="minutes-attendee-chip">{a}</span>
                    ))}
                  </span>
                )
                : <span className="minutes-muted">{tMinutes('minutes.form.no_attendees')}</span>}
            </dd>
          </div>
        </dl>
      </section>
      {/* 보낸 이력(additive) — 양식정리 아래, 원문 위. 되돌리기가 없어 이것이 유일한 되짚을 길이다. */}
      <MinutesSentHistory hook={sends} />
    </>
  );
}

/**
 * 「요약」 탭 — ★ 표가 아니라 ⭐ «글과 줄»이다 (사용자 결정 ② · 시안 3절).
 *
 * ⚠ 「전체」 탭의 양식도 ★ 같은 셋(요약·결정사항·액션아이템)을 담는다. ⇒ ⛔ 베낀 것이 아니다 —
 *   ⭐ 사용자가 ★ 「다른 꼴」로 정했다: 그쪽은 ⭐ «뽑는 표»이고 이쪽은 ★ «읽는 글»이다.
 *
 * ## ⛔ 「액션아이템」을 ★ 「할 일」로 ⭐ 고치지 «않았다»
 *
 * 시안 3절의 ★ 그림은 ⭐ 「할 일」이라 적고 있다. ⇒ ⚠ 그런데 그것은 ★ 그림의 ⭐ 쉬운 말이고,
 * 시안 5절의 ★ «열쇠 표»에는 ⭐ 그 이름이 «없다» — ⇒ ★ 즉 ⭐ 낱말을 바꾸기로 «정한 적이 없다».
 * ⇒ ⛔ 그리고 `minutes.section.actions` 는 ★ 「전체」 탭 양식도 ⭐ 쓰므로, 여기서 고치면
 *   ★ 뽑는 문서의 낱말까지 ⭐ 함께 바뀐다. ⇒ ✅ 그래서 ★ 있는 말을 ⭐ 그대로 쓰고 ★ 물어 둔다.
 *
 * ## ⚠ 지금은 ★ 「요약이 없습니다」까지다
 *
 * 시안은 빈 자리에 ★ 「요약을 진행해주세요.」 ＋ ⭐ 「요약하기」 단추를 그렸다. ⇒ ⛔ 그 단추는
 * `summarizeVoiceSession(session_id)` 이라 ★ «세션 id»가 필요하고, `MinutesDetail` 에는 ⭐ 그 칸이
 * «없다»(세션 쪽이 `minutes_id` 를 들고 있어 ★ 되짚어야 한다). ⇒ ⇒ ★ 그 배선은 ⭐ 다음 커밋이다.
 *   ⇒ ★ 그때까지는 ⭐ 있는 말(`minutes.detail.no_summary` — 「요약이 없습니다.」)을 쓴다.
 *   ⇒ ⚠ 「진행해주세요」를 ★ 단추 없이 ⭐ 먼저 띄우지 «않는다» — 시안 5절이 그 규율을 적었다:
 *     「★ 그 말을 받는 사람이 ⭐ 지금 그 자리에서 ★ 그것을 할 수 «있나»」.
 */
/**
 * 「요약」 탭이 ★ 「요약하기」를 ⭐ 보일 수 있나 — ⇒ 판정 ★ 한 곳.
 *
 * ⛔ 요약이 ★ 이미 있으면 ⭐ 안 보인다. ⇒ 까닭이 ★ 서버에 있다: `minutes-svc` 가 나중 요약을
 *   ⭐ «대기(pending)» 회의록에만 채운다(`DO UPDATE … WHERE status='pending'`).
 *   ⇒ ★ 다시 눌러도 ⭐ «아무것도 안 바뀐다» ⇒ ⇒ 눌리게 두면 ★ «거짓 단추»다.
 *   (앱이 같은 까닭으로 끈다 — `aos/.../MinutesDetailSummarize.kt` 머리말)
 * ⛔ 그리고 ★ 녹음 세션이 ⭐ 없으면 «길이 없다» — ⇒ 웹에 `regenerate` 를 부르는 파일이 ★ 0 이다.
 *   ⇒ ⚠ 그때는 ⭐ 「요약이 없습니다.」까지만 말한다(사용자 결정 ⑪ 대기).
 *
 * ## ⚠⚠ `!hasSummary` 는 ★ «점»에 걸린다 — ⇒ ⭐ 패널은 «삼항 순서»가 이미 막는다
 *
 * RED 로 재서 알았다: 이 함수에서 `!hasSummary` 를 ★ 빼도 ⇒ ⛔ 패널에는 ⭐ 단추가 «안 뜬다» —
 * `MinutesSummaryPanel` 의 삼항이 ★ 「요약이 있으면 글」을 ⭐ «먼저» 보기 때문이다.
 * ⇒ ⇒ ★ 즉 두 검사가 ⭐ 겹쳐 있고 ⇒ ★ 뒤 검사(이 함수)의 그 조건은 ⭐ **점에만** 걸린다.
 * ⇒ ⇒ ⇒ ⛔ 그러니 ★ 「패널이 막으니 여기선 빼도 된다」고 읽지 ⭐ 말 것 — ★ 빼면 ⇒ ⭐ 요약이 있는
 *   회의록의 「요약」 탭에 ★ 점이 찍히고 ⇒ ⇒ ⭐ 「할 일이 있다」는 ★ 거짓을 말한다.
 * ⇒ ⇒ ⇒ ⇒ ✅ 그래서 RED 도 ★ 둘을 ⭐ «동시에» 깨서 잰다(조건 ＋ 삼항 순서).
 */
function canRunSummary(detail: MinutesDetail): boolean {
  const hasSummary = detail.summary != null && detail.summary !== '';
  const sid = detail.voice_session_id;
  return !hasSummary && typeof sid === 'string' && sid !== '';
}

/**
 * 「요약」 탭 — ⭐ 빈 자리의 ★ 「요약하기」 (2026-10-09 · 관제 답 ⓐ · 시안 3절).
 *
 * ## ⭐ 길은 ★ 「음성 갈래」 ⭐ 하나다
 *
 *     voice_session_id 있음  ⇒ ★ POST /voice/sessions/:id/summarize — ⭐ 비동기 · 끝나면 알림
 *     없음(직접 작성)        ⇒ ⛔ 웹엔 길이 «없다» ⇒ 「요약이 없습니다.」
 *
 * ⚠ 앱은 `POST /minutes/:id/regenerate` 로 직접 작성도 요약한다. ⇒ ⛔ 그 끝점은 ★ 서버에 «있지만»
 *   ⭐ 동기이고(`await generateMinutes` → `reply.send`) ★ 원문이 비면 ⭐ 400 이라
 *   ⇒ ⚠ 음성 회의록(원문 NULL)에는 ★ 애초에 못 쓴다. ⇒ ⇒ 웹에 붙일지는 ⭐ 사용자 결정 ⑪ 다.
 *
 * ## ⚠ 누르기 «전»에 ★ 두 번 막는다 — ⇒ ⭐ 목록 줄과 «같은» 판정으로
 *
 *     0자        `hasNothingToSummarize`  ⇒ ⛔ 서버를 «안» 부르고 말한다
 *     실패 조각  `summaryWouldStall`      ⇒ ⛔ 같다 — 서버 물높이가 그 자리에서 멈춘다
 *     모른다     세션 줄이 ★ 없으면        ⇒ ⭐ 서버에 맡긴다 (「모른다」는 「0」이 아니다)
 *
 * ⇒ ⛔ 그 판정을 ★ 여기서 ⭐ 다시 적지 않는다 — `awaiting-summary.ts` 한 곳이다.
 * ⇒ ⇒ ⚠ 0자를 안 막으면 ★ 「시작했습니다」 뒤 ⭐ «영영»이 된다(0자는 요약이 안 생겨 알림도 안 온다).
 *
 * ## ⚠ 「시작했습니다」 ★ 뒤의 한 줄은 ⭐ 안 붙인다 — ⛔ 그 갈래(`listHint`)는 2026-10-20 걷었다(MinutesSummarizeSay 머리말)
 *
 * 그 글자는 ⭐ 「… 이 **목록에** 회의록으로 올라옵니다」라 ⇒ ⛔ 상세에서는 ★ 틀린 말이다.
 * ⇒ ★ 상세용 한 줄(「요약이 완료되면 알려드립니다.」)은 ⭐ **A-2.5** 에서 선다 —
 *   `minutes.awaiting.*` 의 ko 를 ★ 앱 상수와 **한 판**으로 바꾸는 판이고 ⇒ ⛔ 내 몫이 아니다.
 */
function MinutesSummaryPanel({ detail, session, summaryAvailable }: {
  detail: MinutesDetail;
  /** 이 회의록을 만든 녹음 세션 줄 — ⭐ 못 찾으면 `null`(「모른다」). */
  session: VoiceSessionItem | null;
  /** 요약 엔진이 꺼져 있으면 눌러도 워커가 **미룬다**(실패가 아니다) ⇒ 미리 말한다. */
  summaryAvailable: boolean;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<SummarizeSaid | null>(null);
  const sid = detail.voice_session_id;

  const press = useCallback(async () => {
    if (typeof sid !== 'string' || sid === '') return;
    // ⭐ 세션 줄을 «알 때만» 미리 막는다 — 모르면 서버에 맡긴다(위 머리말).
    if (session !== null) {
      /*
       * ⭐⭐ 2026-10-20 (사용자 결정 · 시안 ②) — 서버가 실패 조각만 다시 받아 적고 그래도 안 되는 것만 빼고 요약한다(#2553).
       *   retrying ⇒ 서버를 «안» 부르고 「다시 받아 적고 있습니다」 · skipped ⇒ 부른다(「n개는 빼고」는 말 부품이 붙인다)
       *   stall(옛 서버 · gave_up_chunks 칸 없음) ⇒ 종전 「요약이 끝까지 가지 못합니다」(summaryWouldStall 의 뜻 그대로).
       * ⚠ retrying 을 0자보다 «먼저» 본다 — 조각이 «전부» 실패해 다시 받아 적는 중이면 글자는 0 이지만 곧 생길 수 있다
       *   (「요약할 글이 없습니다」는 그때 틀린 말이다).
       */
      const fs = failedChunkState(session);
      if (fs === 'retrying') { setSaid('retrying'); return; }
      if (hasNothingToSummarize(session)) { setSaid('no_text'); return; }
      if (fs === 'stall' && summaryWouldStall(session)) { setSaid('cannot_finish'); return; }
    }
    setBusy(true);
    setSaid(null);
    try {
      await summarizeVoiceSession(sid);
      // ⛔ `queued` 가 false 여도 «같게» 다룬다 — 「이미 큐에 있다」는 뜻이다.
      setSaid('queued');
    } catch (e) {
      setSaid(axios.isAxiosError(e) && e.response?.status === 404 ? 'gone' : 'failed');
    } finally {
      setBusy(false);
    }
  }, [sid, session]);

  return (
    <section className="minutes-block minutes-sum-block">
      {detail.summary != null && detail.summary !== ''
        ? <p className="minutes-sum-text">{detail.summary}</p>
        : canRunSummary(detail) && detailVoiceIsEmpty(detail.voice, session)
          ? (
            /*
             * ⭐⭐ 2026-10-20 (사용자 결정 · 시안 ③) — 글자 0 이고 전사가 끝난 녹음: 「내부에 녹음된 내역이 없습니다.」 하나 ·
             *   「요약하기」 없음. ⚠ 웹에는 녹음 지우기가 없다(모바일만 — 회의록 «삭제»는 상세 머리에 그대로 있다).
             */
            <p className="minutes-sum-text minutes-voice-empty" data-session-id={sid}>{tMinutes('minutes.voice.empty')}</p>
          )
        : canRunSummary(detail)
          ? (
            <div className="minutes-sum-run">
              <p className="minutes-sum-run-head">{tMinutes('minutes.detail.summary_empty')}</p>
              <p className="minutes-sum-run-why">{tMinutes('minutes.detail.summary_empty_why')}</p>
              <p className="minutes-sum-run-why">{tMinutes('minutes.detail.summary_source_hint')}</p>
              {/* ⚠ 엔진이 꺼져 있어도 ★ 단추는 «눌린다» — 워커가 미루는 것이지 실패가 아니다.
                  ⇒ 그 사실을 ⭐ 누르기 «전»에 말한다(목록 줄과 같은 꼴). */}
              {!summaryAvailable && (
                <span className="minutes-awaiting-say minutes-awaiting-say--warn" role="note">
                  {tMinutes('minutes.awaiting.no_engine')}
                </span>
              )}
              <button
                type="button"
                className="minutes-awaiting-btn"
                onClick={() => void press()}
                disabled={busy || said === 'queued'}
                data-session-id={sid}
              >
                {busy
                  ? <Loader2 size={13} className="minutes-spin" aria-hidden />
                  : <Sparkles size={13} aria-hidden />}
                {tMinutes('minutes.awaiting.summarize')}
              </button>
              <MinutesSummarizeSay
                said={said}
                s={session ?? { failed_chunks: 0, received_chunks: 0, transcribed_chunks: 0 }}
              />
            </div>
          )
          : <p className="minutes-muted">{tMinutes('minutes.detail.no_summary')}</p>}

      {detail.decisions.length > 0 && (
        <div className="minutes-sum-sec">
          <h4 className="minutes-sum-sec-title">{tMinutes('minutes.section.decisions')}</h4>
          <ol className="minutes-sum-list">
            {detail.decisions.map((d, i) => <li key={i}>{d}</li>)}
          </ol>
        </div>
      )}

      {detail.action_items.length > 0 && (
        <div className="minutes-sum-sec">
          <h4 className="minutes-sum-sec-title">{tMinutes('minutes.section.actions')}</h4>
          <ul className="minutes-sum-tasks">
            {detail.action_items.map((a, i) => (
              <li key={i} className="minutes-sum-task">
                <span className="minutes-sum-task-text">{a.text}</span>
                {a.assignee != null && a.assignee !== '' && (
                  <span className="minutes-assignee-chip">{a.assignee}</span>
                )}
                {a.due != null && a.due !== '' && (
                  <span className="minutes-due-chip">{a.due}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** 녹음 시작부터의 밀리초 → ★ 사람이 읽는 시각. ⇒ ⭐ 한 시간을 넘으면 시를 붙인다. */
function elapsedLabel(ms: number): string {
  const t = Math.max(0, Math.floor(ms / 1000));
  const s = t % 60, m = Math.floor(t / 60) % 60, h = Math.floor(t / 3600);
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return (h > 0 ? `${h}:` : '') + `${mm}:${String(s).padStart(2, '0')}`;
}

/**
 * 한 조각이 ★ 글 대신 ⭐ «무슨 말»을 받나 — ⇒ ⛔ 실패가 아닌 것을 ★ 실패라 하지 않는다.
 *
 * ⚠ 서버 머리말(`GET …/transcript`)이 못 박은 셋:
 *   `done` ＋ 빈 글 ⇒ ⭐ 「조용한 5분」은 ★ «정상 결과»다
 *   `device_side`   ⇒ ⭐ 단말이 전사했다 ⇒ ★ 서버에 글이 «없다» — ⛔ 실패로 세지 않는다
 *   `skipped`       ⇒ ⭐ «말단»이다 — ★ 되살릴 것도 없다
 * ⇒ ⛔ 그리고 `pending`·`running` 을 ★ «합치지 않는다» — 서버가 그 까닭을 적었다.
 * ⇒ ⭐ 글이 «있으면» ★ `null` 이다(그때는 글을 그린다).
 */
function chunkNoteKey(c: TranscriptChunk): string | null {
  if (c.text !== '') return null;
  if (c.status === 'device_side') return 'minutes.full.chunk_device';
  if (c.status === 'failed') return 'minutes.full.chunk_failed';
  if (c.status === 'skipped') return 'minutes.full.chunk_skipped';
  if (c.status === 'pending') return 'minutes.full.chunk_pending';
  if (c.status === 'running') return 'minutes.full.chunk_running';
  // `done` ＋ 빈 글 — ⭐ 조용한 구간이다.
  return 'minutes.full.chunk_quiet';
}

/**
 * ★★★ 「전체」 탭 — ⭐ **시간별 전사 ＋ 녹음 내려받기** (2026-10-09 · 사용자 결정 ⓑ).
 *
 * ## ⭐ 녹음 ★ 네 꼴 — ⇒ 서버 소스로 쟀다
 * ```
 *   audio_ready true           ⇒ ★ 내려받기 ⭐ 단추
 *   null ＋ status ≠ 'failed'   ⇒ ⭐ 「잇는 중」 — ⛔ 실패가 «아니다»(voice-merge.ts:72 「대기다」)
 *   status 'failed'            ⇒ ★ 합본 실패 — ⚠ 까닭 둘(상한 초과 · 압축 실패)을 ⭐ 서버가 «안 가른다»
 *   status 'purged' · 세션 모름 ⇒ ⛔ 아무것도 안 그린다
 * ```
 * ⚠ 합본 상한은 ★ 400MB ≈ ⭐ **3시간 28분**(16kHz mono PCM16) — `voice-merge.ts` 의 그 상수.
 *
 * ## ⚠ 권한이 ★ «작성자만»이다 ⇒ ⛔ 그래서 ★ 404 를 «떠들지 않는다»
 *
 * 서버가 `getOwnedSession` 으로 보고 ⇒ 남의 것은 **404** 다. ⇒ `getVoiceTranscript` 가
 * ⭐ `null` 을 돌려주고 ⇒ ★ 여기서 「아직 전사된 글이 없습니다」로 그린다.
 * ⇒ ⚠ 그리고 ★ 세션 줄(`session`)을 ⭐ 못 찾으면 ⇒ ★ 내려받기 자리를 ⭐ «안 세운다» —
 *   `audio_file_id` 를 ★ 모르는 채 단추를 세우면 ⇒ ⛔ 눌러서 404 를 보게 된다.
 *
 * ## ⚠ 「다 됐나」는 ★ `transcriptComplete` ⭐ 한 곳이다
 *
 * ⛔ `joined >= total` «만» 보면 ★ 꼬리가 안 올라온 세션을 ⭐ 「다 됐다」로 읽는다
 *   (그 사고를 서버 머리말이 적고 있다). ⇒ ★ 그래서 ⭐ 그 함수를 쓰고,
 *   ⇒ 덜 됐으면 ★ 「아직 올라오는 중」을 ⭐ 적는다.
 */
function MinutesTranscriptPanel({ sessionId, session }: {
  sessionId: string;
  /** 녹음 상태를 아는 유일한 길 — ⛔ `null` 이면 「모른다」다(내려받기를 안 세운다). */
  session: VoiceSessionItem | null;
}): JSX.Element {
  const [tr, setTr] = useState<VoiceTranscript | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failedSay, setFailedSay] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void getVoiceTranscript(sessionId).then((r) => {
      if (cancelled) return;
      setTr(r);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [sessionId]);

  const audioId = session?.audio_file_id ?? null;
  const press = useCallback(async () => {
    if (audioId === null || audioId === '') return;
    setBusy(true);
    setFailedSay(false);
    try {
      const res = await requestPresignedDownload(audioId, { purpose: 'download' });
      // ★ 이 레포의 관용구 그대로 — `AttachmentList` 의 그 여섯 줄이다(브라우저가 직접 GET).
      const a = document.createElement('a');
      a.href = res.downloadUrl;
      a.download = res.name;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setFailedSay(true);
    } finally {
      setBusy(false);
    }
  }, [audioId]);

  if (loading) {
    return (
      <section className="minutes-block minutes-tr-block">
        <Loader2 size={18} className="minutes-spin" aria-hidden />
      </section>
    );
  }

  const chunks = tr?.chunks ?? [];
  const purged = session?.status === 'purged';
  /* ⭐ 2026-10-20 (시안 ①) — 합본 실패 표지(merge_failed)면 «잇는 중»이 아니다(영영 안 끝난다) — 아래 failedJoin 이 말한다. */
  const joining = session !== null && !purged && !session.audio_ready && session.status !== 'failed'
    && session.merge_failed !== true;
  /*
   * ⭐ 2026-10-20 (시안 ① · 서버 #2554) — 합본 실패가 이제 상태(failed)가 아니라 `merge_failed` 로 온다(회의록은 선다).
   *   ⇒ 둘 중 하나면 「녹음을 잇지 못했습니다」. 옛 서버(칸 없음)는 종전처럼 status 로 읽는다.
   */
  const failedJoin = session !== null && !purged && !session.audio_ready
    && (session.merge_failed === true || session.status === 'failed');
  const canDownload = session !== null && !purged && session.audio_ready && audioId !== null && audioId !== '';

  return (
    <section className="minutes-block minutes-tr-block">
      {/* ⭐ 녹음 — 네 꼴(위 머리말). ⛔ 모르면·파기면 아무것도 그리지 않는다. */}
      {canDownload && (
        <div className="minutes-tr-audio">
          <button type="button" className="minutes-awaiting-btn" onClick={() => void press()} disabled={busy}>
            {busy
              ? <Loader2 size={13} className="minutes-spin" aria-hidden />
              : <Download size={13} aria-hidden />}
            {tMinutes(busy ? 'minutes.full.downloading' : 'minutes.full.download')}
          </button>
          {failedSay && (
            <span className="minutes-awaiting-say minutes-awaiting-say--warn" role="alert">
              {tMinutes('minutes.full.download_failed')}
            </span>
          )}
        </div>
      )}
      {joining && (
        <p className="minutes-muted" role="note">{tMinutes('minutes.full.audio_joining')}</p>
      )}
      {failedJoin && (
        <p className="minutes-muted" role="note">{tMinutes('minutes.full.audio_failed')}</p>
      )}

      <div className="minutes-tr-head">
        <h3>{tMinutes('minutes.full.transcript')}</h3>
      </div>
      {chunks.length === 0
        ? <p className="minutes-muted">{tMinutes('minutes.full.empty')}</p>
        : (
          <ol className="minutes-tr-list">
            {chunks.map((c) => {
              const note = chunkNoteKey(c);
              return (
                <li key={c.seq} className="minutes-tr-row">
                  <span className="minutes-tr-at">{elapsedLabel(c.start_ms)}</span>
                  {note === null
                    ? <span className="minutes-tr-text">{c.text}</span>
                    : <span className="minutes-tr-note">{tMinutes(note as never)}</span>}
                </li>
              );
            })}
          </ol>
        )}
      {/* ⚠ 덜 올라왔으면 ★ 그 사실을 적는다 — ⛔ 「다 됐다」로 보이면 반쪽인 줄 모른다. */}
      {tr !== null && chunks.length > 0 && !transcriptComplete(tr) && (
        <p className="minutes-muted" role="note">{tMinutes('minutes.full.partial')}</p>
      )}
    </section>
  );
}
/**
 * ★★★ 상세의 ⭐ **탭 넷** — 「회의정보 · 요약 · 전체」 (2026-10-08 사용자 결정 · 시안 2절).
 *
 * ```
 * 처음 열면   ⭐ 「회의정보」 — ★ 사용자가 그렇게 정했다
 * 모양        ⭐ `line`(주 탭) — ⛔ `pill` 이 «아니다»
 * ```
 * ⚠ 모양이 규칙이다 — 정본 `Tabs` 머리말이 적고 있다: `line` 은 ★ «다른 내용»으로 가는 주 탭,
 *   `pill` 은 ⭐ «같은 목록을 거르는» 전환이다. ⇒ 이 셋은 ★ 다른 내용이라 `line` 이다.
 *   ⇒ ✅ 그리고 ★ 목록의 탭(「전체 / 완료 / 정리 대기」)은 ⭐ 손으로 짠 `.minutes-tabs` 라
 *     ★ 생김새가 ⭐ 이미 다르다 — ⇒ 사용자가 거르개를 ★ 「다른 화면」으로 읽지 않는다.
 *
 * ⚠ `showSource` 는 ★ 부모(`MinutesDetailView`)가 쥔다 — 탭을 돌아와도 ⭐ 「원문 보기」가 접히지
 *   않게(사용자 결정 ⑦). 그 까닭은 #2328 의 `MinutesDetailBody` 머리말에 적었다.
 */
const DETAIL_TABS = ['info', 'summary', 'doc', 'full'] as const;
type DetailTab = (typeof DETAIL_TABS)[number];

/**
 * 그 회의록이 ★ 보일 탭 — ⛔ 녹음이 «없으면» ⭐ 「전체」를 세우지 않는다.
 *
 * ⚠ 「전체」는 ★ 시간별 전사 ＋ 녹음이다. ⇒ ⛔ 직접 쓴 회의록에는 ★ 그 둘이 ⭐ «아예 없다».
 * ⇒ ★ 늘 세우면 ⇒ ⭐ 「빈 탭」을 눌러 보게 된다. ⇒ ⇒ ★ 이 파일의 선례와 ⭐ 같은 결이다 —
 *   「만든 방법」도 ★ 모르면 ⭐ 숨기고, 「요약하기」도 ★ 할 수 없으면 ⭐ 안 띄운다.
 * ⇒ ⚠ 그래서 ★ 탭 수가 ⭐ 회의록마다 다르다(녹음 ⇒ 넷 · 직접 작성 ⇒ 셋). ⇒ 그것이 ★ «사실»이다.
 */
function detailTabsFor(detail: MinutesDetail): readonly DetailTab[] {
  const sid = detail.voice_session_id;
  const hasRecording = typeof sid === 'string' && sid !== '';
  return hasRecording ? DETAIL_TABS : DETAIL_TABS.filter((t) => t !== 'full');
}

function MinutesDetailBody({ detail, copiedKey, copy, sends, showSource, setShowSource, session, summaryAvailable, summaryReady, onApplySummary }: {
  detail: MinutesDetail;
  copiedKey: string | null;
  copy: (k: string, t: string) => void;
  sends: ReturnType<typeof useMinutesSends>;
  showSource: boolean;
  setShowSource: React.Dispatch<React.SetStateAction<boolean>>;
  session: VoiceSessionItem | null;
  summaryAvailable: boolean;
  summaryReady: boolean;
  onApplySummary: () => void;
}): JSX.Element {
  const [tab, setTab] = useState<DetailTab>('info');
  const tabs = detailTabsFor(detail);
  /*
   * ⚠ 고른 탭이 ★ 그 회의록에 ⭐ «없을» 수 있다 — ⇒ 「전체」를 보다가 ★ 직접 쓴 회의록으로
   *   옮겨 가면(「보기」·다른 줄) ⇒ ⭐ 그 탭이 사라진다. ⇒ ⛔ 그때 ★ 빈 자리가 남으면 안 된다.
   */
  const shown: DetailTab = tabs.includes(tab) ? tab : 'info';
  return (
    <>
      <Tabs
        className="minutes-dtabs"
        variant="line"
        ariaLabel={tMinutes('minutes.dtab.aria')}
        value={shown}
        onChange={(v) => setTab(v as DetailTab)}
        items={tabs.map((t) => ({
          value: t,
          label: (
            <>
              {tMinutes(`minutes.dtab.${t}` as const)}
              {/*
                ⭐ 점 — 「요약」 탭에 ★ «할 일이 있을 때만» 찍는다 (2026-10-09 · 관제가 받은 권고).
                ⚠ 시안 2절은 「요약이 ★ 없으면 점」이었다. ⇒ ⛔ 그런데 ⭐ «할 수 있는 일이 없는데»
                  점을 찍으면 ⇒ ★ 「누르라는데 ⭐ 누를 것이 없다」가 된다 — 직접 쓴 회의록은
                  웹에 요약 길이 «없다». ⇒ ⇒ ✅ 그래서 ★ `canRunSummary` 와 ⭐ 같은 판정을 쓴다.
                ⚠ 그래서 ★ 점과 단추는 ⭐ «늘 함께» 뜨고 ★ 함께 사라진다 — 시험이 그것을 잠근다.
              */}
              {t === 'summary' && canRunSummary(detail) && (
                <span className="minutes-dtab-dot" aria-hidden />
              )}
            </>
          ),
        }))}
      />
      {/*
        ★★★ 띠 — ⭐ 「요약이 끝났습니다 · 보기」 (2026-10-08 사용자 결정 ⑦ · 시안 4절).

        ## ⚠ 자리가 ★ «탭 아래»다 — ⇒ ⭐ 어느 탭을 보고 있어도 보인다
        시안 4절의 두 그림이 ★ 탭 줄 ⭐ «밑»에 띠를 그렸다. ⇒ ⭐ 그리고 그것이 맞다 —
        사용자가 ★ 「전체」 탭에서 긴 원문을 읽는 중일 수도 있다.

        ## ⚠ 「보기」가 ★ 하는 일 — ⇒ ⭐ 쥐고 있던 것을 «넣는다»
        ⛔ 조회를 또 부르지 않는다 — 부모가 ★ 이미 다시 읽어 ⭐ 손에 쥐고 있다.
        ⇒ 그 까닭은 `AiMinutesPage` 의 `summaryReady` 머리말에 있다.
      */}
      {summaryReady && (
        <InlineNote tone="success" className="minutes-sum-ready">
          <span>{tMinutes('minutes.detail.summary_ready')}</span>
          <button type="button" className="minutes-sum-ready-btn" onClick={onApplySummary}>
            {tMinutes('minutes.detail.summary_ready_view')}
          </button>
        </InlineNote>
      )}
      {shown === 'info' && <MinutesInfoPanel detail={detail} sends={sends} />}
      {shown === 'summary' && (
        <MinutesSummaryPanel detail={detail} session={session} summaryAvailable={summaryAvailable} />
      )}
      {shown === 'doc' && (
        <MinutesDocPanel
          detail={detail}
          copiedKey={copiedKey}
          copy={copy}
          showSource={showSource}
          setShowSource={setShowSource}
        />
      )}
      {/*
        ⭐ 새 「전체」 — ★ 시간별 전사 ＋ 녹음. ⇒ ⛔ 녹음이 없으면 ★ 이 탭이 ⭐ 아예 안 선다
          (`detailTabsFor`). ⇒ ⚠ 그래도 ★ 여기서 다시 보는 것은 ⇒ ⭐ 타입을 좁히려는 것이다.
      */}
      {shown === 'full' && detail.voice_session_id != null && detail.voice_session_id !== '' && (
        <MinutesTranscriptPanel sessionId={detail.voice_session_id} session={session} />
      )}
    </>
  );
}

/**
 * 상세의 «머리말» — 제목 · 메타 · 보내기 · 복사 · 지우기 (2026-10-08 빼냄).
 *
 * ## ⛔ 왜 빼냈나 — 「요약 탭」이 오기 «전»에, 그것만 따로
 *
 * 상세에 탭 셋(회의정보 · 요약 · 전체)을 세우는 판이 옵니다. 그때 ★ 머리말은 «어느 탭에서도»
 * 보여야 합니다 — 탭을 옮겼다고 지우기·보내기가 사라지면 안 됩니다.
 * ⇒ 그런데 이 덩이는 `MinutesDetailView` 의 기존 `return` «안»에 있었습니다.
 * ⇒ ⇒ 두 길이 있었고 ★ 이쪽을 골랐습니다:
 *   ⓐ (고름) ⭐ 같은 파일 안 함수로 «글자 그대로» 빼낸다 — 33줄 이동 · ★ DOM 변화 0
 *   ⓑ 새 탭마다 ★ 다시 베낀다 ⇒ ⛔ 보내기 잠금·복사·지우기 배선이 ⭐ 세 곳으로 갈린다
 *      ⇒ 그 셋 중 하나만 고치는 날 ★ 「기존과 다르게 동작」이 생긴다
 * ⇒ CLAUDE.md 「소스 회귀 금지」의 ★ 적어 둔 «목적»이 ⭐ 「기존 기능이 변경 전과 동일하게
 *   동작함을 보장」이므로, ⓐ 가 그 목적을 ★ 더 잘 지킵니다(글자 변화 0 ＋ DOM 을 맞댈 수 있음).
 *
 * ## ⚠ 이 빼내기가 «안전한가»를 ⭐ 시험이 잽니다
 *
 * `minutes-head-extract.test.tsx` 가 ★ 빼내기 «전»에 떠 둔 DOM 글자와 맞댑니다(소유자·비소유자 둘).
 * ⇒ ⛔ 이 함수에서 class 하나·title 하나만 바뀌어도 ★ 빨개집니다(사보타주로 확인했습니다).
 *
 * ⚠ 바뀐 것은 ★ 한 줄뿐입니다 — `onClick={() => setSendOpen(true)}` ⇒ `onClick={onOpenSend}`.
 *   부르는 쪽이 그 함수를 그대로 넘기므로 ★ 하는 일은 같고 ⭐ DOM 에도 안 나타납니다.
 */
function MinutesDetailHead({ detail, fullText, copiedKey, copy, onOpenSend, sendBlocked, isOwner, onDelete }: {
  detail: MinutesDetail;
  fullText: string;
  copiedKey: string | null;
  copy: (key: string, text: string) => void;
  onOpenSend: () => void;
  sendBlocked: boolean;
  isOwner: boolean;
  onDelete: () => void;
}): JSX.Element {
  return (
  <header className="minutes-detail-head">
    <div className="minutes-detail-headtext">
      <h2 className="minutes-detail-title">{detail.title}</h2>
      <div className="minutes-detail-meta">
        {detail.meeting_date && (
          <span className="minutes-chip"><Calendar size={13} aria-hidden />{formatDate(detail.meeting_date)}</span>
        )}
        <OriginBadge origin={detail.origin} />
        <StatusBadge status={detail.status} />
        {detail.model && <span className="minutes-chip minutes-chip--muted">{tMinutes('minutes.detail.model', { name: detail.model })}</span>}
      </div>
    </div>
    <div className="minutes-detail-actions">
      <CopyButton id="all" text={fullText} copiedKey={copiedKey} copy={copy} />
      {/* 보내기(additive) — 서버가 아직 그 길을 안 냈으면 잠그고 까닭을 title 로 읽어 준다. */}
      <button
        type="button"
        className="minutes-btn-send"
        onClick={onOpenSend}
        disabled={sendBlocked}
        title={sendBlocked ? tMinutes('minutes.send.not_ready') : undefined}
      >
        <Send size={14} aria-hidden /> {tMinutes('minutes.send.open')}
      </button>
      {isOwner && (
        <button type="button" className="minutes-btn-delete" onClick={onDelete}>
          <Trash2 size={14} aria-hidden /> {tMinutes('minutes.btn.delete')}
        </button>
      )}
    </div>
  </header>
  );
}

/**
 * ⭐ 이 회의록을 만든 ★ 녹음 세션 줄을 ⭐ 찾는다 — ⇒ 없으면 `null`(「모른다」).
 *
 * ⚠ 「없다」와 ★ 「모른다」를 ⭐ 가르지 «못한다» — `listVoiceSessions()` 가 돌려준 묶음 안에서만 찾는다.
 * ⇒ ★ 그래서 못 찾으면 ⭐ 막지 않고 «서버에 맡긴다»(그 판정은 `MinutesSummaryPanel` 머리말).
 */
function findSessionOf(detail: MinutesDetail, sessions: readonly VoiceSessionItem[]): VoiceSessionItem | null {
  const sid = detail.voice_session_id;
  if (typeof sid !== 'string' || sid === '') return null;
  return sessions.find((s) => s.session_id === sid) ?? null;
}

function MinutesDetailView({ detail, onDelete, sessions, summaryAvailable, summaryReady, onApplySummary }: {
  detail: MinutesDetail;
  onDelete: () => void;
  sessions: readonly VoiceSessionItem[];
  summaryAvailable: boolean;
  /** 요약이 끝나 ★ 띠로 알릴 것이 ⭐ 있나. */
  summaryReady: boolean;
  /** 「보기」 — 쥐고 있던 것을 ★ 화면에 ⭐ 넣는다. */
  onApplySummary: () => void;
}): JSX.Element {
  const session = findSessionOf(detail, sessions);
  /*
   * ★★★ 원문이 ⭐ «있으면» ⇒ ★ **펼친 채로** 연다 (2026-10-09 ★ 사용자 결정 ㉰).
   *
   * ## ⭐ 왜 — ★ 직접 쓴 회의록은 ⇒ ⭐ 그 글이 «본문 전부»다
   *
   * ⛔ 접혀 있으면 ⇒ ★ 사용자가 ⭐ 「내 회의록이 ★ 비었다」로 읽는다.
   * ⇒ ⚠ 반면 ★ 녹음에서 온 회의록은 ⇒ ⭐ 원문이 «없어» ★ 접을 것이 ⭐ 애초에 없다
   *   (⇒ 그때는 `none_voice` ＋ `voice_kept` 두 문단이 선다).
   * ⇒ ⇒ ★ 그래서 ⭐ 「늘 펼침」도 「늘 접힘」도 ★ 아니고 ⇒ ⭐ **원문 유무가 정한다.**
   *
   * ## ⚠ 「접기」 단추는 ★ 남겼다 — ⇒ ⭐ 다만 «기본이 펼침»이다
   *
   * 붙여넣은 원문은 ★ 10만 자까지다(`SOURCE_MAX`). ⇒ ⭐ 긴 글을 접고 싶을 수 있다.
   * ⇒ ★ 글자도 이미 둘이다(`minutes.source.show` · `minutes.source.hide`) ⇒ ⭐ 새 열쇠 0.
   *
   * ## ⭐ 왜 ★ 효과가 ⭐ «없어도» 되나 — ⇒ 이 부품이 ★ 다시 mount 된다
   *
   * `AiMinutesPage` 는 ★ `detail` 이 `null` 일 때 ⭐ 이 부품을 «안 그린다»(`: detail ? (`).
   * ⇒ `openDetail` 이 ★ 먼저 `setDetail(null)` 을 하므로 ⇒ ⭐ 다른 회의록을 열면
   *   ★ **다시 mount 되고** ⇒ ⇒ ⭐ 이 초기값이 ★ 다시 정해진다.
   * ⇒ ⇒ ⭐⭐ 그리고 ★ 띠의 「보기」는 ⇒ ⭐ `setDetail(…)` 만 하고 ★ `null` 을 «안 거친다»
   *   ⇒ ⇒ ★ 그래서 ⭐ 사용자가 «손으로 접은 것»을 ⛔ 되돌리지 않는다
   *   ⇒ ⇒ ⇒ ★ 그것이 ⭐ 사용자 결정 ⑦(읽던 자리)과 ★ 같은 결이다.
   * ⇒ ⚠ 효과로 했으면 ⇒ ⛔ 「보기」를 누를 때마다 ★ 접은 것이 ⭐ 다시 펼쳐졌을 것이다.
   */
  const [showSource, setShowSource] = useState(() => detail.source_text != null && detail.source_text !== '');
  // ★ 결재 갈래를 기안 화면으로 넘기는 데 쓴다 — 부품이 아니라 «화면»이 라우터를 안다.
  const navigate = useNavigate();
  // 보내기 단계에 들어갔나(additive). 켜지면 이 모달이 «보내기 화면»으로 갈린다 — 모달을 겹치지 않는다.
  const [sendOpen, setSendOpen] = useState(false);
  /**
   * 방금 보낸 것에 서버가 붙여 준 «사실» — `logged`(이력에 적혔나) · `truncated`(줄여 보냈나).
   *
   * ★ 보내면 패널이 닫히므로 이 알림은 **상세에 남아야** 한다. 패널 안에서 말하면 사람이 못 본다.
   */
  const [lastAck, setLastAck] = useState<MinutesSendAck | null>(null);
  /**
   * 그 ack 에서 **말해도 되는 것만** 걸러 낸 것 — 판정은 `describeSentAck` 한 곳에 있다
   * (그래야 「모르는 수를 만들지 않는다」 같은 규칙을 **제 시험**으로 잠글 수 있다).
   */
  const sentSay = useMemo(() => (lastAck === null ? null : describeSentAck(lastAck)), [lastAck]);
  const { copiedKey, copy } = useCopy();
  // 삭제는 작성자(owner)만 — 비소유자에겐 버튼 미노출(모바일 파리티). owner_user_id 는
  // wc_at sub=String(users.id) 와 동일 공간이라 authStore user.id 문자열과 비교.
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  const isOwner = myId !== '' && myId === detail.owner_user_id;

  const fullText = useMemo(() => {
    const lines: string[] = [`# ${detail.title}`];
    if (detail.summary) lines.push('', `## ${tMinutes('minutes.section.summary')}`, detail.summary);
    if (detail.decisions.length) {
      lines.push('', `## ${tMinutes('minutes.section.decisions')}`);
      detail.decisions.forEach((d, i) => lines.push(`${i + 1}. ${d}`));
    }
    if (detail.action_items.length) {
      lines.push('', `## ${tMinutes('minutes.section.actions')}`);
      detail.action_items.forEach((a) => {
        const meta = [a.assignee, a.due].filter(Boolean).join(', ');
        lines.push(`- [ ] ${a.text}${meta ? ` (${meta})` : ''}`);
      });
    }
    return lines.join('\n');
  }, [detail]);


  /*
   * 보내기(additive) — 아래 기존 return 은 그대로 두고, 보내기 중일 때만 «앞에서» 돌려준다.
   * ★ 이력은 상세를 열 때 함께 읽는다 — 서버가 아직 그 길을 안 냈으면 `not-served` 가 오고,
   *   그것으로 보내기 단추를 잠근다(누르고 나서 알면 늦다).
   */
  const sends = useMinutesSends(detail.id);
  const sendBlocked = sends.state === 'not-served';
  if (sendOpen) {
    return (
      <div className="minutes-detail-inner">
        <MinutesSendPanel
          minutesId={detail.id}
          attendees={detail.attendees}
          source={detail}
          sendReady={sends.state === 'loading' ? null : !sendBlocked}
          /* ★ 이미 보낸 이력을 패널에 내려 준다 — 「또 누르는 것」은 패널 «안»에서 읽혀야 막힌다. */
          priorSends={sends.items}
          onApprovalHandoff={({ title, content }) => {
            /*
             * ★★ 결재 갈래 — **기안 화면으로 넘어간다**(2026-09-22 사용자 결정 「채널마다 다르게」).
             *
             * ★ 주소가 아니라 **라우터 state** 로 넘긴다: 그 화면이 이미 그렇게 받고 있고
             *   (`ApprovalNewPage.tsx` 의 `useLocation().state`), 그 파일 주석이 까닭을 적어 뒀다 —
             *   「제목에 «/» 나 «?» 가 들어가는 일이 흔하고, 주소에 실으면 그때 깨진다」.
             * ⛔ `contentType:'TEXT'` 를 함께 준다 — 회의록 글은 **줄바꿈이 뜻을 갖는다**(HTML 이면
             *   요약·결정·액션이 한 덩이로 붙는다. 서버 메일 갈래도 같은 까닭으로 TEXT 를 쓴다).
             * ★ 이력은 **서버가 기안을 만들 때** 적는다 — 화면이 따로 적지 않는다.
             *
             * ## ★ 「기안 화면에서 «종류»를 고르게 되어 있다」 — 그것이 «설계»다 (2026-09-22 확인 답)
             *
             * 운영 확인에서 「문서와 다르다」고 본 자리다. 실제로 `state` 에 **`docTypeId` 를 싣지
             * 않는다** — 회의록에는 그 회사의 결재 문서 종류를 고를 근거가 **아무것도 없다**
             * (종류 목록은 기관마다 다르고 켜짐/꺼짐도 바뀐다). 우리가 골라 주면 **틀린 종류로
             * 상신**되고, 그것은 사람이 고치기 어려운 잘못이다.
             * ⇒ 제목·본문·회의록 id 만 넘기고 **종류는 사람이 고른다.** ⛔ 결함이 아니다.
             */
            navigate('/approval/new', {
              state: { title, content, contentType: 'TEXT', minutesId: detail.id },
            });
          }}
          onBack={() => setSendOpen(false)}
          onSent={(ack) => {
            /*
             * ★★★ 보낸 뒤 «가짜 줄»을 만들지 않는다 — 201 응답과 이력 한 줄은 모양이 다르다
             *   (201 엔 `sent_at`·`id` 가 없다). 그래서 이력을 **다시 읽는다**: 화면에 보이는 것이
             *   «실제로 남은 것»이어야 한다(되돌리기가 없으니 그것이 유일한 되짚을 길이다).
             * ⚠ `logged === false` 면 적히지 «않았다» — 읽어도 없으니 부르지 않고, 아래 알림으로 말한다.
             *   ⛔ `undefined`(모른다)를 `false` 로 접지 않는다 — 안 남았는데 남았다고 보이는 것보다
             *     모를 때 잠자코 있는 편이 낫다.
             */
            setLastAck(ack);
            if (ack.logged !== false) sends.reload();
            setSendOpen(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className="minutes-detail-inner">
      <MinutesDetailHead
        detail={detail}
        fullText={fullText}
        copiedKey={copiedKey}
        copy={copy}
        onOpenSend={() => setSendOpen(true)}
        sendBlocked={sendBlocked}
        isOwner={isOwner}
        onDelete={onDelete}
      />

      {detail.status === 'failed' && (
        <div className="minutes-failed-notice" role="alert">{tMinutes('minutes.detail.failed_notice')}</div>
      )}

      {/*
        ★★★ **방금 보냈다고 «말한다»** (2026-09-22 운영 결함 — 「발송되었습니다」가 하나도 안 나왔다).

        ⛔ 종전 판은 `logged === false` 와 `truncated` «둘만» 말해서, 정상 경로에서는 **아무 말도
          안 했다.** 「전달됨」을 피한 것은 옳았지만 한 걸음 지나쳤다 — 「보냈다」는 **서버가 아는
          사실**이고(201 이 `recipients`·`room_id`·`mail_id`·`logged` 로 말해 준다), 안 말하면
          사람이 **한 번 더 누른다**(방 둘 · 메일 두 통 · 되돌리기 없음).

        ★ 자리가 «여기»인 까닭: 종전 알림은 양식정리 표 **아래**에 있어서, 보내고 돌아온 사람의
          눈에 **안 들어오는 자리**였다. 머리 바로 밑으로 올린다.
        ★ `role="status"` — 읽어 주는 화면에서도 들리게 한다(⛔ `alert` 는 아니다. 성공이니까).
      */}
      {sentSay !== null && (
        <InlineNote tone={sentSay.notLogged ? 'warning' : 'success'} className="minutes-sent-notice">
          <strong>{
            sentSay.count !== null
              ? tMinutes(sentSay.channel === 'mail' ? 'minutes.send.sent_mail' : 'minutes.send.sent_message', { n: sentSay.count })
              : tMinutes(sentSay.channel === 'mail' ? 'minutes.send.sent_mail_nocount' : 'minutes.send.sent_message_nocount')
          }</strong>
          {sentSay.mailId !== null && (
            <span className="minutes-sent-detail">{tMinutes('minutes.send.sent_mail_id', { id: sentSay.mailId })}</span>
          )}
          {/* ⚠ 「보냈다」와 「이력에 안 남았다」는 **함께** 말해야 한다 — 하나만 말하면 반쪽이다. */}
          {sentSay.notLogged && <span className="minutes-sent-detail">{tMinutes('minutes.send.not_logged')}</span>}
          {sentSay.truncated && <span className="minutes-sent-detail">{tMinutes('minutes.send.sent_truncated')}</span>}
        </InlineNote>
      )}

      <MinutesDetailBody
        detail={detail}
        copiedKey={copiedKey}
        copy={copy}
        sends={sends}
        showSource={showSource}
        setShowSource={setShowSource}
        session={session}
        summaryAvailable={summaryAvailable}
        summaryReady={summaryReady}
        onApplySummary={onApplySummary}
      />
    </div>
  );
}

function MinutesCreateForm({
  onCreated,
  onCancel,
}: {
  onCreated: (row: MinutesDetail) => void;
  onCancel: () => void;
}): JSX.Element {
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [attendees, setAttendees] = useState('');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 파일 첨부 절삭 안내 + 502(AI 정리 실패) 보존행 — 모바일 파리티(sourceNotice / aiFailed).
  const [sourceNotice, setSourceNotice] = useState<string | null>(null);
  const [aiFailed, setAiFailed] = useState<{ row: MinutesDetail | null; reason: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const attendeeChips = useMemo(
    () => attendees.split(',').map((s) => s.trim()).filter(Boolean),
    [attendees],
  );
  const canSubmit = title.trim().length > 0 && source.trim().length > 0 && !busy;

  // 텍스트 파일(.txt/.md/.csv) 첨부 → 원문에 병합. 음성 파일은 웹 미지원(모바일 앱 전용).
  // 제목 비면 파일명으로 채우고, 상한 초과분은 잘라 안내(모바일 appendTextFile 파리티).
  const onPickFile = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 같은 파일 재선택 허용
    if (!file) return;
    const isText = file.type.startsWith('text/') || TEXT_FILE_RE.test(file.name);
    if (!isText) { setErr(tMinutes('minutes.err.file_not_text')); return; }
    try {
      const raw = (await file.text()).trim();
      if (!raw) { setErr(tMinutes('minutes.err.file_empty')); return; }
      setErr(null);
      setSource((prev) => {
        const merged = prev.trim() ? `${prev}\n${raw}` : raw;
        const capped = capSource(merged);
        setSourceNotice(capped.length < merged.length ? tMinutes('minutes.create.source_capped') : null);
        return capped;
      });
      setTitle((prev) => (prev.trim() ? prev : fileBaseName(file.name).slice(0, 200)));
    } catch {
      setErr(tMinutes('minutes.err.file_read'));
    }
  };

  const submit = async (): Promise<void> => {
    if (!title.trim()) { setErr(tMinutes('minutes.err.title_required')); return; }
    if (!source.trim()) { setErr(tMinutes('minutes.err.source_required')); return; }
    // 서버 상한 방어 — 원문이 상한 초과면 잘라 400(validation) 회피 + 안내(모바일 submit 파리티).
    const capped = capSource(source);
    if (capped.length < source.length) {
      setSource(capped);
      setSourceNotice(tMinutes('minutes.create.source_capped'));
    }
    setBusy(true);
    setErr(null);
    setAiFailed(null);
    try {
      const row = await createMinutes({
        title: title.trim().slice(0, 200),
        meeting_date: meetingDate || undefined,
        attendees: attendeeChips,
        source_text: capped,
      });
      onCreated(row);
    } catch (e) {
      if (e instanceof MinutesAiFailedError) {
        // 원문은 서버에 status='failed' 로 보존됨 — 재시도 or 저장된 항목 보기 제공.
        setAiFailed({ row: e.minutes, reason: e.reason });
      } else {
        setErr(tMinutes('minutes.err.generate_failed', { msg: (e as Error)?.message ?? '' }));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="minutes-create" aria-label={tMinutes('minutes.btn.new')}>
      <header className="minutes-create-head">
        <h2><span className="minutes-create-ico"><Sparkles size={16} aria-hidden /></span> {tMinutes('minutes.btn.new')}</h2>
      </header>

      <div className="minutes-create-hint"><Sparkles size={14} aria-hidden /> <span>{tMinutes('minutes.create.hint')}</span></div>

      <div className="minutes-create-fields">
        <label className="minutes-field minutes-field--full">
          <span className="minutes-field-lbl"><FileText size={13} aria-hidden /> {tMinutes('minutes.field.title')} <i className="minutes-field-req">*</i></span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={tMinutes('minutes.placeholder.title')} maxLength={200} />
        </label>

        <div className="minutes-field-row">
          <label className="minutes-field">
            <span className="minutes-field-lbl"><Calendar size={13} aria-hidden /> {tMinutes('minutes.field.date')}</span>
            <input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} />
          </label>
          <label className="minutes-field">
            <span className="minutes-field-lbl"><Users size={13} aria-hidden /> {tMinutes('minutes.field.attendees')}</span>
            <input value={attendees} onChange={(e) => setAttendees(e.target.value)} placeholder={tMinutes('minutes.placeholder.attendees')} />
          </label>
        </div>
        {attendeeChips.length > 0 && (
          <div className="minutes-attendees minutes-attendees--preview">
            <Users size={13} aria-hidden />
            {attendeeChips.map((a, i) => <span key={i} className="minutes-attendee-chip">{a}</span>)}
          </div>
        )}

        <label className="minutes-field minutes-field--grow minutes-field--full">
          <span className="minutes-field-labelrow">
            <span className="minutes-field-lbl"><FileText size={13} aria-hidden /> {tMinutes('minutes.field.source')} <i className="minutes-field-req">*</i></span>
            <span className={`minutes-charcount${source.length > SOURCE_MAX ? ' is-over' : ''}`}>
              {tMinutes('minutes.create.charcount', { n: source.length, max: SOURCE_MAX })}
            </span>
          </span>
          <textarea value={source} onChange={(e) => setSource(e.target.value)} placeholder={tMinutes('minutes.placeholder.source')} />
        </label>

        <div className="minutes-source-attach">
          <button type="button" className="minutes-attach-btn" onClick={() => fileInputRef.current?.click()} disabled={busy}>
            <Paperclip size={13} aria-hidden /> {tMinutes('minutes.btn.attach')}
          </button>
          <span className="minutes-attach-hint">{tMinutes('minutes.create.attach_hint')}</span>
          <input
            ref={fileInputRef} type="file"
            accept=".txt,.md,.markdown,.csv,.log,.json,.text,text/*"
            style={{ display: 'none' }}
            onChange={(e) => void onPickFile(e)}
          />
        </div>
        {sourceNotice && <div className="minutes-source-notice" role="status">{sourceNotice}</div>}
      </div>

      {err && <div className="minutes-error" role="alert">{err}</div>}

      {aiFailed && (
        <div className="minutes-aifailed" role="alert">
          <p className="minutes-aifailed-msg">{tMinutes('minutes.create.ai_failed')}</p>
          {aiFailed.reason && (
            <p className="minutes-aifailed-reason">{tMinutes('minutes.create.ai_failed_reason', { msg: aiFailed.reason })}</p>
          )}
          {aiFailed.row && (
            <button type="button" className="minutes-btn-secondary" onClick={() => onCreated(aiFailed.row as MinutesDetail)}>
              <FileText size={14} aria-hidden /> {tMinutes('minutes.btn.view_saved')}
            </button>
          )}
        </div>
      )}

      <div className="minutes-create-actions">
        <button type="button" className="minutes-btn-secondary" onClick={onCancel} disabled={busy}>
          {tMinutes('minutes.btn.cancel')}
        </button>
        <button type="button" className="minutes-btn-primary" onClick={() => void submit()} disabled={!canSubmit}>
          {busy ? <Loader2 size={15} className="minutes-spin" aria-hidden /> : <Sparkles size={15} aria-hidden />}
          {busy ? tMinutes('minutes.btn.generating') : tMinutes('minutes.btn.generate')}
        </button>
      </div>
    </div>
  );
}
